import React, { useEffect, useMemo, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneMissed, AlertTriangle, ShieldAlert, Briefcase, User, Bot, CheckCircle2, Calendar, Play, ArrowLeft, ChevronRight, ChevronDown, ChevronLeft, Car, Moon, WifiOff, Video, Shield, Phone, MessageSquare, Brain, Heart, Activity, Mic, Zap, PhoneIncoming, PhoneOff, Search, BoxIcon, Plus, Users, Globe, Settings, Clock } from 'lucide-react';
// --- Constants & Data ---
const PHASES = [{
  id: 1,
  duration: 3000
}, {
  id: 2,
  duration: 5000
}, {
  id: 3,
  duration: 4000
}, {
  id: 4,
  duration: 4000
}, {
  id: 5,
  duration: 6000
}, {
  id: 6,
  duration: 6000
}, {
  id: 7,
  duration: 4000
}, {
  id: 8,
  duration: 7000
}, {
  id: 9,
  duration: 5000
}, {
  id: 10,
  duration: 38000
}, {
  id: 11,
  duration: 6000
}, {
  id: 12,
  duration: 5000
}, {
  id: 13,
  duration: 4000
}, {
  id: 14,
  duration: 6000
}, {
  id: 15,
  duration: 5000
}, {
  id: 16,
  duration: 4000
}, {
  id: 17,
  duration: 4000
}, {
  id: 18,
  duration: 6000
}, {
  id: 19,
  duration: 5000
}, {
  id: 20,
  duration: 6000
}, {
  id: 21,
  duration: 9000
}, {
  id: 22,
  duration: 6000
}, {
  id: 23,
  duration: 10000
}, {
  id: 24,
  duration: 7000
}, {
  id: 25,
  duration: 6000
}, {
  id: 26,
  duration: 6000
}, {
  id: 27,
  duration: 6000
}, {
  id: 28,
  duration: 10000
}, {
  id: 29,
  duration: 8000
}, {
  id: 30,
  duration: 7000
}, {
  id: 31,
  duration: 6000
}, {
  id: 32,
  duration: 6000
}];
const TOTAL_PHASES = PHASES.length;
const NOTIFICATIONS = [{
  id: 1,
  name: 'Mom',
  type: 'Family',
  time: 'Just now',
  icon: User,
  color: 'text-blue-400',
  bg: 'bg-blue-400/10'
}, {
  id: 2,
  name: 'Dr. Ahmed',
  type: 'Important',
  time: '2m ago',
  icon: AlertTriangle,
  color: 'text-red-400',
  bg: 'bg-red-400/10'
}, {
  id: 3,
  name: "School - Sarah's Teacher",
  type: 'Urgent',
  time: '5m ago',
  icon: AlertTriangle,
  color: 'text-orange-400',
  bg: 'bg-orange-400/10'
}, {
  id: 4,
  name: '+1 (555) 012-3456',
  type: 'Unknown',
  time: '12m ago',
  icon: PhoneMissed,
  color: 'text-zinc-400',
  bg: 'bg-zinc-400/10'
}, {
  id: 5,
  name: 'Spam Likely',
  type: 'Spam',
  time: '15m ago',
  icon: ShieldAlert,
  color: 'text-red-500',
  bg: 'bg-red-500/10'
}, {
  id: 6,
  name: 'Client - Mike Ross',
  type: 'Business',
  time: '1h ago',
  icon: Briefcase,
  color: 'text-purple-400',
  bg: 'bg-purple-400/10'
}, {
  id: 7,
  name: 'Bank Security',
  type: 'Critical',
  time: '2h ago',
  icon: ShieldAlert,
  color: 'text-red-500',
  bg: 'bg-red-500/10'
}];
const UNAVAILABLE_STATES = [{
  id: 'meeting',
  label: 'In a Meeting',
  icon: Video,
  color: 'text-purple-400',
  bg: 'bg-purple-400/10',
  border: 'border-purple-400/20'
}, {
  id: 'driving',
  label: 'Driving Mode',
  icon: Car,
  color: 'text-blue-400',
  bg: 'bg-blue-400/10',
  border: 'border-blue-400/20'
}, {
  id: 'sleeping',
  label: 'Do Not Disturb',
  icon: Moon,
  color: 'text-indigo-400',
  bg: 'bg-indigo-400/10',
  border: 'border-indigo-400/20'
}, {
  id: 'offline',
  label: 'Offline',
  icon: WifiOff,
  color: 'text-zinc-400',
  bg: 'bg-zinc-400/10',
  border: 'border-zinc-400/20'
}, {
  id: 'battery',
  label: 'Battery Dead',
  icon: BoxIcon,
  color: 'text-red-500',
  bg: 'bg-red-500/10',
  border: 'border-red-500/20'
}];
const LIVE_CALL_SCRIPT = [{
  sender: 'ai' as const,
  text: "Hello, you've reached Alex's AI assistant. Alex is currently unavailable. How can I help you?",
  time: '2:30 PM'
}, {
  sender: 'user' as const,
  text: 'Hi, this is Rachel from the legal department. I need to speak with Alex about the NDA review.',
  time: '2:30 PM'
}, {
  sender: 'ai' as const,
  text: 'I understand. I can take a detailed message and flag this as high priority. Could you share more details?',
  time: '2:31 PM'
}, {
  sender: 'user' as const,
  text: "Yes, the NDA for the Meridian partnership needs Alex's sign-off by end of day Friday.",
  time: '2:32 PM'
}, {
  sender: 'ai' as const,
  text: "Got it — Meridian partnership NDA, sign-off needed by Friday EOD. I'll mark this as urgent.",
  time: '2:32 PM'
}, {
  sender: 'user' as const,
  text: "Also, can you let him know there's a clause in Section 4.2 that needs his review specifically?",
  time: '2:33 PM'
}, {
  sender: 'ai' as const,
  text: "Noted — Section 4.2 flagged for Alex's personal review. I'll include that in the summary. Anything else?",
  time: '2:33 PM'
}, {
  sender: 'user' as const,
  text: "That's everything. Please have him call me back when he's free.",
  time: '2:34 PM'
}, {
  sender: 'ai' as const,
  text: "Absolutely, Rachel. I'll send Alex a priority notification right away. Have a great day!",
  time: '2:34 PM'
}];
// Smooth Apple-like easing
const ease = [0.16, 1, 0.3, 1];
interface PresentationScreenProps {
  onEnterApp?: () => void;
}
export function PresentationScreen({
  onEnterApp
}: PresentationScreenProps) {
  const [phase, setPhase] = useState(1);
  const [presentationKey, setPresentationKey] = useState(0);
  const [activeStateIndex, setActiveStateIndex] = useState(0);
  // Phase 10 sub-animation state
  const [callStep, setCallStep] = useState<'list' | 'detail' | 'summary'>('list');
  const [callRowPressed, setCallRowPressed] = useState(false);
  const [visibleMessages, setVisibleMessages] = useState<Array<{
    sender: 'ai' | 'user';
    text: string;
    time: string;
  }>>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [callIsLive, setCallIsLive] = useState(true);
  // Scene 3 state
  const [phoneDigits, setPhoneDigits] = useState('');
  const [otpDigits, setOtpDigits] = useState('');
  const [activatedScenario, setActivatedScenario] = useState<string | null>(null);
  // Scene 4 state
  const [ceStep, setCeStep] = useState<'callsPage' | 'dashboard' | 'addModal' | 'settings' | 'interactions' | 'groups'>('callsPage');
  const [highlightedFeature, setHighlightedFeature] = useState<number | null>(null);
  const [familyGroupName, setFamilyGroupName] = useState('');
  const [ceButtonPressed, setCeButtonPressed] = useState(false);
  const [cePlusPressed, setCePlusPressed] = useState(false);
  // Scene 5 state
  const [calStep, setCalStep] = useState<'chat' | 'calendar' | 'features' | 'ecosystem'>('chat');
  const [highlightedCalFeature, setHighlightedCalFeature] = useState<number | null>(null);
  const [calChatMessages, setCalChatMessages] = useState<Array<{
    sender: 'ai' | 'user';
    text: string;
    time: string;
  }>>([]);
  const [isCalTyping, setIsCalTyping] = useState(false);
  // --- Auto-Advance Logic ---
  useEffect(() => {
    if (phase >= TOTAL_PHASES) return;
    const currentDuration = PHASES[phase - 1].duration;
    const timer = setTimeout(() => {
      setPhase(p => p + 1);
    }, currentDuration);
    return () => clearTimeout(timer);
  }, [phase, presentationKey]);
  // --- Cycling Logic for Phase 8 ---
  useEffect(() => {
    if (phase === 8) {
      const interval = setInterval(() => {
        setActiveStateIndex(prev => (prev + 1) % UNAVAILABLE_STATES.length);
      }, 1200);
      return () => clearInterval(interval);
    }
  }, [phase]);
  // --- Scene 3: Onboarding Animation Sequence ---
  useEffect(() => {
    if (phase < 15 || phase > 20) {
      // Reset Scene 3 state when not in Scene 3
      setPhoneDigits('');
      setOtpDigits('');
      setActivatedScenario(null);
      return;
    }
    const timers: ReturnType<typeof setTimeout>[] = [];
    // Phase 15: Phone number entry
    if (phase === 15) {
      const phoneNumber = '(555) 123-4567';
      phoneNumber.split('').forEach((char, i) => {
        timers.push(setTimeout(() => {
          setPhoneDigits(prev => prev + char);
        }, 800 + i * 100));
      });
    }
    // Phase 16: OTP entry
    if (phase === 16) {
      const otp = '8429';
      otp.split('').forEach((digit, i) => {
        timers.push(setTimeout(() => {
          setOtpDigits(prev => prev + digit);
        }, 1000 + i * 400));
      });
    }
    // Phase 19: Activate Busy scenario
    if (phase === 19) {
      timers.push(setTimeout(() => {
        setActivatedScenario('busy');
      }, 2000));
    }
    return () => timers.forEach(clearTimeout);
  }, [phase, presentationKey]);
  // --- Scene 4: Caller Experience Animation Sequence ---
  useEffect(() => {
    if (phase < 21 || phase > 26) {
      setCeStep('callsPage');
      setHighlightedFeature(null);
      setFamilyGroupName('');
      return;
    }
    const timers: ReturnType<typeof setTimeout>[] = [];
    if (phase === 21) {
      setCeStep('callsPage');
      setCeButtonPressed(false);
      // After 2.5s of pulsing, show the button being pressed
      timers.push(setTimeout(() => {
        setCeButtonPressed(true);
      }, 2500));
      // After press animation, transition to dashboard
      timers.push(setTimeout(() => {
        setCeStep('dashboard');
        setCeButtonPressed(false);
      }, 3500));
    }
    if (phase === 22) {
      // Show + button pressed first
      setCePlusPressed(true);
      timers.push(setTimeout(() => {
        setCePlusPressed(false);
        setCeStep('addModal');
      }, 800));
      const name = 'Family';
      name.split('').forEach((char, i) => {
        timers.push(setTimeout(() => {
          setFamilyGroupName(prev => prev + char);
        }, 1800 + i * 200));
      });
    }
    if (phase === 23) {
      setCeStep('settings');
      // Cycle through 7 features (0-6)
      for (let i = 0; i < 7; i++) {
        timers.push(setTimeout(() => {
          setHighlightedFeature(i);
        }, 1000 + i * 1200));
      }
      // Clear highlight at end
      timers.push(setTimeout(() => {
        setHighlightedFeature(null);
      }, 1000 + 7 * 1200));
    }
    if (phase === 24) {
      setCeStep('interactions');
    }
    if (phase === 25) {
      setCeStep('groups');
    }
    return () => timers.forEach(clearTimeout);
  }, [phase, presentationKey]);
  // --- Scene 5: Calendar & Agentic AI Animation Sequence ---
  useEffect(() => {
    if (phase < 27 || phase > 32) {
      setCalStep('chat');
      setHighlightedCalFeature(null);
      setCalChatMessages([]);
      setIsCalTyping(false);
      return;
    }
    const timers: ReturnType<typeof setTimeout>[] = [];
    if (phase === 28) {
      setCalStep('chat');
      const script = [{
        sender: 'user' as const,
        text: "Hi, I'd like to schedule a meeting with Alex to discuss the new product launch.",
        time: '10:00 AM'
      }, {
        sender: 'ai' as const,
        text: 'I can help with that. Alex is available tomorrow at 1:30 PM or 3:00 PM. Which works better for you?',
        time: '10:01 AM'
      }, {
        sender: 'user' as const,
        text: '1:30 PM works perfectly.',
        time: '10:02 AM'
      }, {
        sender: 'ai' as const,
        text: "Great! I've scheduled a Client Discovery Call for tomorrow at 1:30 PM. I'll send you the calendar invite shortly.",
        time: '10:02 AM'
      }];
      let msgDelay = 1000;
      script.forEach((msg, idx) => {
        timers.push(setTimeout(() => setIsCalTyping(true), msgDelay));
        msgDelay += 1000;
        timers.push(setTimeout(() => {
          setIsCalTyping(false);
          setCalChatMessages(prev => [...prev, msg]);
        }, msgDelay));
        msgDelay += 1500;
      });
    }
    if (phase === 29) {
      setCalStep('calendar');
    }
    if (phase === 30) {
      setCalStep('features');
      // Cycle through 4 features (0-3)
      for (let i = 0; i < 4; i++) {
        timers.push(setTimeout(() => {
          setHighlightedCalFeature(i);
        }, 500 + i * 1500));
      }
      timers.push(setTimeout(() => {
        setHighlightedCalFeature(null);
      }, 500 + 4 * 1500));
    }
    if (phase === 31) {
      setCalStep('ecosystem');
    }
    return () => timers.forEach(clearTimeout);
  }, [phase, presentationKey]);
  // --- Phase 10: Calls Module Animation Sequence ---
  useEffect(() => {
    if (phase !== 10) {
      // Reset when leaving phase 10
      setCallStep('list');
      setVisibleMessages([]);
      setIsTyping(false);
      setCallIsLive(true);
      return;
    }
    const timers: ReturnType<typeof setTimeout>[] = [];
    setCallRowPressed(false);
    // 0-2s: Show list, 2s: Press the call row, 3s: Transition to detail
    timers.push(setTimeout(() => setCallRowPressed(true), 2000));
    timers.push(setTimeout(() => {
      setCallRowPressed(false);
      setCallStep('detail');
    }, 3000));
    // 4.5s onwards: Start delivering messages
    let msgDelay = 4500;
    LIVE_CALL_SCRIPT.forEach((msg, idx) => {
      // Show typing indicator
      timers.push(setTimeout(() => setIsTyping(true), msgDelay));
      msgDelay += 1200;
      // Show message
      const capturedIdx = idx;
      timers.push(setTimeout(() => {
        setIsTyping(false);
        setVisibleMessages(prev => [...prev, LIVE_CALL_SCRIPT[capturedIdx]]);
      }, msgDelay));
      msgDelay += 1800;
    });
    // After all messages: end call
    timers.push(setTimeout(() => {
      setCallIsLive(false);
    }, msgDelay + 500));
    // Stay on detail view — summary card appears automatically when callIsLive becomes false
    return () => timers.forEach(clearTimeout);
  }, [phase, presentationKey]);
  // --- Particle System ---
  const particles = useMemo(() => {
    return Array.from({
      length: 60
    }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      delay: Math.random() * 2,
      duration: Math.random() * 4 + 3
    }));
  }, []);
  // --- Handlers ---
  const handleSkip = () => setPhase(TOTAL_PHASES);
  const handleReplay = () => {
    setPhase(1);
    setPresentationKey(k => k + 1);
  };
  const handleBackToApp = () => {
    if (onEnterApp) onEnterApp();
  };
  return <div key={presentationKey} className="fixed inset-0 z-[50] bg-black text-white overflow-hidden font-sans selection:bg-vexu/30" data-id="element-1500">
      {/* Ambient Background Gradient */}
      <motion.div className="absolute inset-0 opacity-40" animate={{
      background: phase >= 5 && phase < 12 || phase >= 15 && phase < 26 || phase >= 28 && phase < 32 ? 'radial-gradient(circle at 50% 50%, rgba(0, 200, 83, 0.15) 0%, rgba(0,0,0,1) 70%)' : phase >= 32 ? 'radial-gradient(circle at 50% 50%, rgba(0, 200, 83, 0.25) 0%, rgba(0,0,0,1) 80%)' : 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.03) 0%, rgba(0,0,0,1) 70%)'
    }} transition={{
      duration: 2,
      ease
    }} data-id="element-1501" />

      {/* Particle Field */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-1502">
        {particles.map(p => <motion.div key={p.id} className="absolute rounded-full" initial={{
        left: `${p.x}vw`,
        top: `${p.y}vh`,
        width: p.size,
        height: p.size,
        opacity: 0,
        backgroundColor: '#ffffff'
      }} animate={{
        left: phase >= 5 && phase < 8 ? '50vw' : `${p.x + (Math.random() * 4 - 2)}vw`,
        top: phase >= 5 && phase < 8 ? '50vh' : `${p.y + (Math.random() * 4 - 2)}vh`,
        opacity: phase >= 5 && phase < 8 ? 0 : phase >= 32 ? [0.2, 0.8, 0.2] : [0.1, 0.5, 0.1],
        backgroundColor: phase >= 5 && phase < 12 || phase >= 15 && phase < 26 || phase >= 28 && phase < 32 ? '#00C853' : '#ffffff',
        scale: phase >= 5 && phase < 8 ? 0 : phase >= 32 ? 1.5 : 1
      }} transition={{
        duration: phase >= 5 && phase < 8 ? 1.5 : p.duration,
        repeat: phase >= 5 && phase < 8 ? 0 : Infinity,
        delay: phase >= 5 && phase < 8 ? Math.random() * 0.5 : p.delay,
        ease: phase >= 5 && phase < 8 ? 'easeIn' : 'easeInOut'
      }} data-id="element-1503" />)}
      </div>

      {/* Main Content Container */}
      <div className="relative w-full h-full flex items-center justify-center" data-id="element-1504">
        {/* --- THE PHONE MOCKUP --- */}
        <motion.div initial={{
        opacity: 0,
        y: 100,
        scale: 0.85
      }} animate={{
        opacity: phase >= 12 && phase < 15 || phase === 27 || phase >= 32 ? 0.1 : 1,
        y: 0,
        scale: phase >= 6 && phase < 12 ? 1.1 : phase >= 12 && phase < 15 || phase === 27 || phase >= 32 ? 0.9 : 1,
        boxShadow: phase >= 5 && phase < 12 || phase >= 15 && phase < 26 || phase >= 28 && phase < 32 ? '0 0 100px rgba(0, 200, 83, 0.2), inset 0 0 30px rgba(0, 200, 83, 0.08)' : '0 0 60px rgba(255, 255, 255, 0.02), inset 0 0 20px rgba(255, 255, 255, 0.02)',
        borderColor: phase >= 5 && phase < 12 || phase >= 15 && phase < 26 || phase >= 28 && phase < 32 ? 'rgba(0, 200, 83, 0.3)' : 'rgba(255, 255, 255, 0.1)',
        filter: phase >= 12 && phase < 15 || phase === 27 || phase >= 32 ? 'blur(20px)' : phase === 7 ? 'blur(8px)' : 'blur(0px)'
      }} transition={{
        duration: 2,
        ease
      }} className="relative w-[320px] h-[680px] rounded-[56px] border-[6px] bg-[#050505] overflow-hidden z-10" data-id="element-1505">
          {/* Phone Notch */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-black rounded-b-3xl z-50 border-b border-x border-white/5" data-id="element-1506" />

          {/* Phase 2-4: The Chaos (Notifications) */}
          <AnimatePresence data-id="element-1507">
            {phase >= 2 && phase < 5 && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: phase >= 3 ? 0.2 : 1,
            filter: phase >= 3 ? 'blur(4px)' : 'blur(0px)'
          }} exit={{
            opacity: 0,
            scale: 1.2,
            filter: 'blur(12px)'
          }} transition={{
            duration: 1.5,
            ease
          }} className="absolute inset-0 p-4 pt-14 flex flex-col gap-3 overflow-hidden" data-id="element-1508">
                {NOTIFICATIONS.map((notif, i) => {
              const Icon = notif.icon;
              return <motion.div key={notif.id} initial={{
                opacity: 0,
                y: 40,
                scale: 0.9,
                rotateX: 15
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1,
                rotateX: 0,
                x: phase === 4 ? Math.random() * 4 - 2 : 0
              }} transition={{
                delay: i * 0.4,
                duration: 0.8,
                ease,
                x: {
                  repeat: Infinity,
                  duration: 0.1,
                  repeatType: 'mirror' as const
                }
              }} className="bg-[#111] backdrop-blur-xl border border-white/10 rounded-2xl p-3.5 flex items-center gap-3 shadow-2xl" data-id="element-1509">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${notif.bg}`} data-id="element-1510">
                        <Icon className={`w-5 h-5 ${notif.color}`} data-id="element-1511" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-1512">
                        <div className="flex justify-between items-center mb-0.5" data-id="element-1513">
                          <span className="text-sm font-semibold text-white truncate" data-id="element-1514">
                            {notif.name}
                          </span>
                          <span className="text-[10px] text-zinc-500" data-id="element-1515">
                            {notif.time}
                          </span>
                        </div>
                        <span className="text-xs text-zinc-400" data-id="element-1516">
                          Missed Call
                        </span>
                      </div>
                    </motion.div>;
            })}
              </motion.div>}
          </AnimatePresence>

          {/* Phase 5: The Transformation (Heartbeat Pulse + Logo Reveal) */}
          <AnimatePresence data-id="element-1517">
            {phase === 5 && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="absolute inset-0 flex items-center justify-center" data-id="element-1518">
                {/* Heartbeat rings */}
                {[1, 2, 3, 4].map(i => <motion.div key={i} className="absolute rounded-full border border-vexu/40" style={{
              width: 60,
              height: 60
            }} animate={{
              scale: [1, 6, 8],
              opacity: [0.8, 0.3, 0],
              borderWidth: ['1px', '3px', '1px']
            }} transition={{
              duration: 2.4,
              repeat: Infinity,
              delay: i * 0.5,
              ease: 'easeOut',
              times: [0, 0.6, 1]
            }} data-id="element-1519" />)}

                {/* Vexu Logo emerging from behind */}
                <motion.div initial={{
              scale: 0.3,
              opacity: 0,
              filter: 'blur(20px)'
            }} animate={{
              scale: 1,
              opacity: 1,
              filter: 'blur(0px)'
            }} transition={{
              delay: 1,
              duration: 2,
              ease
            }} className="relative z-10 flex flex-col items-center" data-id="element-1520">
                  <motion.img src="https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg" alt="Vexu Logo" className="w-20 h-20 drop-shadow-[0_0_30px_rgba(0,200,83,0.6)]" animate={{
                scale: [1, 1.05, 1]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-1521" />
                  <motion.p initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 2.5,
                duration: 1,
                ease
              }} className="mt-4 text-xl font-bold tracking-tight text-white" data-id="element-1522">
                    Vexu.ai
                  </motion.p>
                </motion.div>

                {/* Center glow */}
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.5,
              duration: 1
            }} className="absolute w-4 h-4 bg-vexu rounded-full shadow-[0_0_60px_30px_rgba(0,200,83,0.4)] z-0" data-id="element-1523" />
              </motion.div>}
          </AnimatePresence>

          {/* Phase 6-7: The Reveal (Dashboard) */}
          <AnimatePresence data-id="element-1524">
            {phase >= 6 && phase <= 7 && <motion.div initial={{
            opacity: 0,
            scale: 0.9,
            filter: 'blur(10px)'
          }} animate={{
            opacity: phase === 7 ? 0.3 : 1,
            scale: 1,
            filter: phase === 7 ? 'blur(8px)' : 'blur(0px)'
          }} exit={{
            opacity: 0,
            scale: 1.1,
            filter: 'blur(10px)'
          }} transition={{
            duration: 1.5,
            ease
          }} className="absolute inset-0 bg-[#0A0A0A] flex flex-col" data-id="element-1525">
                <div className="pt-14 pb-6 px-6 flex flex-col items-center border-b border-white/5 bg-gradient-to-b from-vexu/10 to-transparent" data-id="element-1526">
                  <img src="https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg" alt="Vexu Logo" className="w-12 h-12 mb-3 drop-shadow-[0_0_15px_rgba(0,200,83,0.5)]" data-id="element-1527" />
                  <h2 className="text-xl font-bold tracking-tight text-white mb-1" data-id="element-1528">
                    Vexu.ai
                  </h2>
                  <p className="text-[10px] uppercase tracking-widest text-vexu font-semibold" data-id="element-1529">
                    Your AI Assistant
                  </p>
                </div>
                <div className="flex-1 p-4 space-y-3 overflow-hidden" data-id="element-1530">
                  <motion.div initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.5,
                duration: 0.8,
                ease
              }} className="bg-[#141414] border border-vexu/30 rounded-2xl p-4 shadow-[0_0_20px_rgba(0,200,83,0.05)]" data-id="element-1531">
                    <div className="flex items-center gap-2 mb-3" data-id="element-1532">
                      <Bot className="w-4 h-4 text-vexu" data-id="element-1533" />
                      <span className="text-xs font-bold text-vexu uppercase tracking-wider" data-id="element-1534">
                        AI Handled
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-white mb-1" data-id="element-1535">
                      Client - Mike Ross
                    </p>
                    <p className="text-xs text-zinc-400 leading-relaxed" data-id="element-1536">
                      "Hi, I need to reschedule our meeting to Thursday at 2 PM.
                      Please confirm."
                    </p>
                    <div className="mt-3 flex items-center gap-2" data-id="element-1537">
                      <span className="px-2 py-1 rounded-md bg-white/5 text-[10px] text-zinc-300 flex items-center gap-1" data-id="element-1538">
                        <Calendar className="w-3 h-3" data-id="element-1539" /> Rescheduled
                      </span>
                    </div>
                  </motion.div>
                  <motion.div initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.8,
                duration: 0.8,
                ease
              }} className="bg-[#141414] border border-white/5 rounded-2xl p-4" data-id="element-1540">
                    <div className="flex items-center gap-2 mb-3" data-id="element-1541">
                      <ShieldAlert className="w-4 h-4 text-red-500" data-id="element-1542" />
                      <span className="text-xs font-bold text-red-500 uppercase tracking-wider" data-id="element-1543">
                        Spam Blocked
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-white mb-1" data-id="element-1544">
                      +1 (555) 012-3456
                    </p>
                    <p className="text-xs text-zinc-500" data-id="element-1545">
                      Auto-declined by Vexu AI security.
                    </p>
                  </motion.div>
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* Phase 8: Unavailable States */}
          <AnimatePresence data-id="element-1546">
            {phase === 8 && <motion.div initial={{
            opacity: 0,
            scale: 1.1
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            filter: 'blur(10px)'
          }} transition={{
            duration: 1,
            ease
          }} className="absolute inset-0 bg-[#050505] flex flex-col items-center justify-center p-6" data-id="element-1547">
                <div className="relative w-full h-64 flex items-center justify-center" data-id="element-1548">
                  <AnimatePresence mode="wait" data-id="element-1549">
                    <motion.div key={activeStateIndex} initial={{
                  opacity: 0,
                  y: 20,
                  scale: 0.9
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -20,
                  scale: 0.9
                }} transition={{
                  duration: 0.5,
                  ease
                }} className={`absolute w-full max-w-[240px] aspect-square rounded-full border ${UNAVAILABLE_STATES[activeStateIndex].border} ${UNAVAILABLE_STATES[activeStateIndex].bg} flex flex-col items-center justify-center p-6 backdrop-blur-xl shadow-2xl`} data-id="element-1550">
                      {createElement(UNAVAILABLE_STATES[activeStateIndex].icon, {
                    className: `w-16 h-16 ${UNAVAILABLE_STATES[activeStateIndex].color} mb-4`
                  })}
                      <span className="text-lg font-bold text-white tracking-wide text-center" data-id="element-1551">
                        {UNAVAILABLE_STATES[activeStateIndex].label}
                      </span>
                    </motion.div>
                  </AnimatePresence>
                </div>
                <div className="absolute bottom-16 w-full px-6 flex flex-col gap-2" data-id="element-1552">
                  <motion.div animate={{
                opacity: [0, 1, 0],
                y: [10, 0, -10]
              }} transition={{
                duration: 1.2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-center gap-3" data-id="element-1553">
                    <PhoneOff className="w-4 h-4 text-zinc-500" data-id="element-1554" />
                    <div className="flex-1" data-id="element-1555">
                      <div className="h-2 w-24 bg-zinc-700 rounded-full mb-1" data-id="element-1556" />
                      <div className="h-1.5 w-16 bg-zinc-800 rounded-full" data-id="element-1557" />
                    </div>
                  </motion.div>
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* Phase 9: VEXU Takeover */}
          <AnimatePresence data-id="element-1558">
            {phase === 9 && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0,
            scale: 1.1
          }} transition={{
            duration: 1,
            ease
          }} className="absolute inset-0 bg-[#0A0A0A] flex flex-col items-center justify-center overflow-hidden" data-id="element-1559">
                <motion.div animate={{
              scale: [1, 2],
              opacity: [0.5, 0]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeOut'
            }} className="absolute w-40 h-40 rounded-full bg-vexu/20 blur-2xl" data-id="element-1560" />
                {/* Vexu App Notification */}
                <motion.div initial={{
              y: -100,
              opacity: 0
            }} animate={{
              y: 40,
              opacity: 1
            }} transition={{
              duration: 0.8,
              ease,
              delay: 0.5
            }} className="absolute top-0 w-[90%] bg-[#111] border border-vexu/20 rounded-2xl p-4 shadow-2xl shadow-vexu/5 z-20" data-id="element-1561">
                  <div className="flex items-center gap-2 mb-3" data-id="element-1562">
                    <div className="w-6 h-6 rounded-lg bg-vexu/20 flex items-center justify-center" data-id="element-1563">
                      <Bot className="w-3.5 h-3.5 text-vexu" data-id="element-1564" />
                    </div>
                    <span className="text-[10px] font-bold text-vexu uppercase tracking-widest" data-id="element-1565">
                      Vexu AI
                    </span>
                    <span className="text-[10px] text-zinc-500 ml-auto" data-id="element-1566">
                      Just now
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-white mb-1" data-id="element-1567">
                    AI Assistant Answered
                  </p>
                  <p className="text-xs text-zinc-400 mb-3" data-id="element-1568">
                    Incoming call from{' '}
                    <span className="text-white font-medium" data-id="element-1569">
                      Legal Department
                    </span>{' '}
                    — Your AI assistant is handling the conversation.
                  </p>
                  <motion.button initial={{
                opacity: 0,
                y: 5
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 1.5,
                duration: 0.5
              }} className="w-full py-2.5 rounded-xl bg-vexu/10 border border-vexu/20 text-vexu text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2" data-id="element-1570">
                    <Activity className="w-3.5 h-3.5" data-id="element-1571" />
                    Show Live Transcription
                  </motion.button>
                </motion.div>

                {/* VEXU Active Badge */}
                <motion.div initial={{
              opacity: 0,
              y: 50,
              scale: 0.8
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} transition={{
              duration: 0.8,
              ease,
              delay: 1.5
            }} className="relative z-30 flex flex-col items-center mt-20" data-id="element-1572">
                  <div className="w-24 h-24 rounded-full bg-vexu/10 border-2 border-vexu flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(0,200,83,0.3)]" data-id="element-1573">
                    <Bot className="w-10 h-10 text-vexu" data-id="element-1574" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1575">
                    VEXU Active
                  </h3>
                  <p className="text-sm text-vexu font-medium tracking-widest uppercase" data-id="element-1576">
                    Answering for you
                  </p>
                  <motion.div initial={{
                height: 0
              }} animate={{
                height: 60
              }} transition={{
                duration: 0.5,
                delay: 2.5
              }} className="w-0.5 bg-gradient-to-b from-vexu to-transparent mt-6" data-id="element-1577" />
                </motion.div>
              </motion.div>}
          </AnimatePresence>

          {/* Phase 10: Realistic Calls Module */}
          <AnimatePresence data-id="element-1578">
            {phase === 10 && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0,
            filter: 'blur(10px)'
          }} transition={{
            duration: 0.8,
            ease
          }} className="absolute inset-0 bg-[#0A0A0A] flex flex-col overflow-hidden" data-id="element-1579">
                <AnimatePresence mode="wait" data-id="element-1580">
                  {/* CALLS LIST VIEW */}
                  {callStep === 'list' && <motion.div key="calls-list" initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -40
              }} transition={{
                duration: 0.3
              }} className="absolute inset-0 flex flex-col" data-id="element-1581">
                      <div className="px-5 pt-14 pb-3 bg-[#0A0A0A]" data-id="element-1582">
                        <div className="flex items-center justify-between mb-3" data-id="element-1583">
                          <h1 className="text-2xl font-bold text-white" data-id="element-1584">
                            Calls
                          </h1>
                          <div className="px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 bg-vexu/10 text-vexu border border-vexu/20" data-id="element-1585">
                            <Users className="w-3.5 h-3.5" data-id="element-1586" />
                            Caller Experience
                          </div>
                        </div>
                        <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-[#141414] border border-white/[0.06]" data-id="element-1587">
                          <Search className="w-4 h-4 flex-shrink-0 text-zinc-500" data-id="element-1588" />
                          <span className="text-[13px] text-zinc-500" data-id="element-1589">
                            Search calls...
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 overflow-hidden" data-id="element-1590">
                        {/* Legal Department - Live */}
                        <motion.div animate={callRowPressed ? {
                    backgroundColor: 'rgba(0,200,83,0.12)',
                    scale: 0.97
                  } : {
                    backgroundColor: ['rgba(0,200,83,0)', 'rgba(0,200,83,0.05)', 'rgba(0,200,83,0)'],
                    scale: 1
                  }} transition={callRowPressed ? {
                    duration: 0.2,
                    ease: [0.16, 1, 0.3, 1]
                  } : {
                    duration: 1.5,
                    delay: 1
                  }} className="w-full px-5 py-3.5 flex items-center gap-3.5 border-b border-white/[0.04] rounded-xl" data-id="element-1591">
                          <div className="relative" data-id="element-1592">
                            <div className="w-11 h-11 rounded-full flex items-center justify-center font-semibold text-[13px] bg-amber-100/60 text-amber-600" data-id="element-1593">
                              LD
                            </div>
                            <div className="absolute -top-0.5 -right-0.5" data-id="element-1594">
                              <span className="inline-flex rounded-full h-3 w-3 bg-vexu border-2 border-[#0A0A0A]" data-id="element-1595" />
                            </div>
                          </div>
                          <div className="flex-1 text-left min-w-0" data-id="element-1596">
                            <div className="flex items-center gap-2" data-id="element-1597">
                              <span className="text-[14px] font-semibold text-white truncate" data-id="element-1598">
                                Legal Department
                              </span>
                              <span className="text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-[5px] bg-vexu/10 text-vexu border border-vexu/20" data-id="element-1599">
                                Live
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 text-[12px] mt-0.5 text-zinc-500" data-id="element-1600">
                              <span className="w-1.5 h-1.5 rounded-full bg-vexu flex-shrink-0" data-id="element-1601" />
                              <span className="font-medium text-vexu" data-id="element-1602">
                                1m 42s
                              </span>
                            </div>
                          </div>
                          <div className="text-[11px] font-medium text-vexu" data-id="element-1603">
                            Now
                          </div>
                        </motion.div>
                        {/* Mike Ross */}
                        <div className="w-full px-5 py-3.5 flex items-center gap-3.5 border-b border-white/[0.04]" data-id="element-1604">
                          <div className="w-11 h-11 rounded-full flex items-center justify-center font-semibold text-[13px] bg-blue-100/60 text-blue-600" data-id="element-1605">
                            MR
                          </div>
                          <div className="flex-1 text-left min-w-0" data-id="element-1606">
                            <span className="text-[14px] font-semibold text-white" data-id="element-1607">
                              Mike Ross
                            </span>
                            <div className="flex items-center gap-1.5 text-[12px] mt-0.5 text-zinc-500" data-id="element-1608">
                              <PhoneIncoming className="w-3 h-3 text-zinc-500" data-id="element-1609" />
                              <span data-id="element-1610">VIP Clients</span>
                              <span className="text-zinc-600" data-id="element-1611">·</span>
                              <span data-id="element-1612">2m 14s</span>
                            </div>
                          </div>
                          <div className="text-[11px] font-medium text-zinc-500" data-id="element-1613">
                            11:15 AM
                          </div>
                        </div>
                        {/* Unknown */}
                        <div className="w-full px-5 py-3.5 flex items-center gap-3.5 border-b border-white/[0.04]" data-id="element-1614">
                          <div className="w-11 h-11 rounded-full flex items-center justify-center font-semibold text-[13px] bg-slate-100/60 text-slate-600" data-id="element-1615">
                            #
                          </div>
                          <div className="flex-1 text-left min-w-0" data-id="element-1616">
                            <span className="text-[14px] font-semibold text-white" data-id="element-1617">
                              +1 (555) 012-3456
                            </span>
                            <div className="flex items-center gap-1.5 text-[12px] mt-0.5 text-zinc-500" data-id="element-1618">
                              <PhoneOff className="w-3 h-3 text-zinc-500" data-id="element-1619" />
                              <span data-id="element-1620">All Contacts</span>
                              <span className="text-zinc-600" data-id="element-1621">·</span>
                              <span data-id="element-1622">45s</span>
                            </div>
                          </div>
                          <div className="text-[11px] font-medium text-zinc-500" data-id="element-1623">
                            Yesterday
                          </div>
                        </div>
                        {/* Design Team */}
                        <div className="w-full px-5 py-3.5 flex items-center gap-3.5 border-b border-white/[0.04]" data-id="element-1624">
                          <div className="w-11 h-11 rounded-full flex items-center justify-center font-semibold text-[13px] bg-emerald-100/60 text-emerald-600" data-id="element-1625">
                            DT
                          </div>
                          <div className="flex-1 text-left min-w-0" data-id="element-1626">
                            <span className="text-[14px] font-semibold text-white" data-id="element-1627">
                              Design Team
                            </span>
                            <div className="flex items-center gap-1.5 text-[12px] mt-0.5 text-zinc-500" data-id="element-1628">
                              <PhoneIncoming className="w-3 h-3 text-zinc-500" data-id="element-1629" />
                              <span data-id="element-1630">Work Contacts</span>
                              <span className="text-zinc-600" data-id="element-1631">·</span>
                              <span data-id="element-1632">5m 30s</span>
                            </div>
                          </div>
                          <div className="text-[11px] font-medium text-zinc-500" data-id="element-1633">
                            Mon
                          </div>
                        </div>
                      </div>
                    </motion.div>}

                  {/* CALL DETAIL VIEW */}
                  {callStep === 'detail' && <motion.div key="calls-detail" initial={{
                opacity: 0,
                x: 40
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: 40
              }} transition={{
                duration: 0.3
              }} className="absolute inset-0 flex flex-col bg-[#0A0A0A]" data-id="element-1634">
                      {/* Header */}
                      <div className="px-4 pt-14 pb-3 flex items-center gap-3 bg-[#141414] border-b border-white/5" data-id="element-1635">
                        <div className="p-1 rounded-full text-zinc-400" data-id="element-1636">
                          <ArrowLeft className="w-5 h-5" data-id="element-1637" />
                        </div>
                        <div className="relative" data-id="element-1638">
                          <div className="w-9 h-9 rounded-full flex items-center justify-center bg-amber-100/60 text-amber-600" data-id="element-1639">
                            <span className="text-xs font-bold" data-id="element-1640">LD</span>
                          </div>
                          <AnimatePresence data-id="element-1641">
                            {callIsLive && <motion.div exit={{
                        opacity: 0,
                        scale: 0
                      }} transition={{
                        duration: 0.4,
                        ease: [0.16, 1, 0.3, 1]
                      }} className="absolute -top-0.5 -right-0.5" data-id="element-1642">
                                <span className="inline-flex rounded-full h-2.5 w-2.5 bg-vexu border-2 border-[#141414]" data-id="element-1643" />
                              </motion.div>}
                          </AnimatePresence>
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-1644">
                          <div className="flex items-center gap-2" data-id="element-1645">
                            <h2 className="text-sm font-bold truncate text-white" data-id="element-1646">
                              Legal Department
                            </h2>
                            <AnimatePresence data-id="element-1647">
                              {callIsLive && <motion.span exit={{
                          opacity: 0,
                          scale: 0.8,
                          width: 0,
                          marginLeft: 0
                        }} transition={{
                          duration: 0.4,
                          ease: [0.16, 1, 0.3, 1]
                        }} className="text-[9px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-md bg-vexu/10 text-vexu border border-vexu/20 overflow-hidden" data-id="element-1648">
                                  Live
                                </motion.span>}
                            </AnimatePresence>
                          </div>
                          <p className={`text-[11px] ${callIsLive ? 'text-vexu' : 'text-zinc-500'}`} data-id="element-1649">
                            {callIsLive ? 'Live · AI Answering' : 'Work Contacts · 2:34 PM'}
                          </p>
                        </div>
                        <div className="flex items-center gap-1" data-id="element-1650">
                          <div className="p-2 rounded-full text-zinc-400" data-id="element-1651">
                            <Phone className="w-4 h-4" data-id="element-1652" />
                          </div>
                        </div>
                      </div>

                      {/* Live Call Banner */}
                      <AnimatePresence data-id="element-1653">
                        {callIsLive && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.8,
                    ease: [0.16, 1, 0.3, 1]
                  }} className="px-4 py-2.5 flex items-center justify-between bg-vexu/[0.03] border-b border-vexu/10 overflow-hidden" data-id="element-1654">
                            <div className="flex items-center gap-2" data-id="element-1655">
                              <div className="flex items-center gap-1.5" data-id="element-1656">
                                <span className="w-1.5 h-1.5 rounded-full bg-vexu" data-id="element-1657" />
                                <span className="text-[10px] font-semibold uppercase tracking-widest text-vexu" data-id="element-1658">
                                  Active Call
                                </span>
                              </div>
                              <span className="text-[10px] text-zinc-500" data-id="element-1659">
                                ·
                              </span>
                              <span className="text-[10px] text-zinc-400" data-id="element-1660">
                                AI Agent handling
                              </span>
                            </div>
                            <div className="flex items-center gap-0.5" data-id="element-1661">
                              {[0, 1, 2, 3].map(i => <motion.div key={i} animate={{
                        height: ['3px', '10px', '5px', '8px', '3px']
                      }} transition={{
                        duration: 1.2,
                        repeat: Infinity,
                        delay: i * 0.15
                      }} className="w-[2px] rounded-full bg-vexu/60" data-id="element-1662" />)}
                            </div>
                          </motion.div>}
                      </AnimatePresence>

                      {/* Chat Messages */}
                      <div className="flex-1 overflow-y-auto flex flex-col justify-end pb-20" data-id="element-1663">
                        <div className="flex justify-center py-3" data-id="element-1664">
                          <span className="text-[10px] font-medium px-3 py-1 rounded-full bg-white/5 text-zinc-500" data-id="element-1665">
                            Now
                          </span>
                        </div>
                        <div className="px-5 space-y-1.5 pb-4" data-id="element-1666">
                          {visibleMessages.map((msg, idx) => <motion.div key={idx} initial={{
                      opacity: 0,
                      y: 6
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      duration: 0.2
                    }} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-1667">
                              <div className={`max-w-[75%] rounded-[14px] px-3 py-2 ${msg.sender === 'user' ? 'bg-vexu text-white rounded-tr-[4px]' : 'bg-[#1A1A1A] text-zinc-100 rounded-tl-[4px] border border-white/[0.04]'}`} data-id="element-1668">
                                <div className="flex items-center gap-2" data-id="element-1669">
                                  <div className={`flex-shrink-0 w-[22px] h-[22px] rounded-full flex items-center justify-center ${msg.sender === 'user' ? 'bg-white/10 text-white/70' : 'bg-white/[0.06] text-zinc-500'}`} data-id="element-1670">
                                    <Play className="w-2.5 h-2.5 ml-[1px]" data-id="element-1671" />
                                  </div>
                                  <p className="text-[12px] leading-[1.45] tracking-[-0.01em]" data-id="element-1672">
                                    {msg.text}
                                  </p>
                                </div>
                                <div className={`flex items-center justify-end gap-1 mt-0.5 ${msg.sender === 'user' ? 'text-white/40' : 'text-zinc-600'}`} data-id="element-1673">
                                  <span className="text-[9px] tracking-wide" data-id="element-1674">
                                    {msg.time}
                                  </span>
                                </div>
                              </div>
                            </motion.div>)}

                          {/* Typing Indicator */}
                          {isTyping && <motion.div initial={{
                      opacity: 0,
                      y: 4
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0
                    }} className="flex justify-start" data-id="element-1675">
                              <div className="rounded-[14px] rounded-tl-[4px] px-3.5 py-2.5 bg-[#1A1A1A] border border-white/[0.04]" data-id="element-1676">
                                <div className="flex gap-[3px] items-center" data-id="element-1677">
                                  {[0, 0.15, 0.3].map((delay, i) => <motion.div key={i} animate={{
                            opacity: [0.3, 0.8, 0.3]
                          }} transition={{
                            duration: 0.9,
                            repeat: Infinity,
                            delay
                          }} className="w-[5px] h-[5px] rounded-full bg-zinc-600" data-id="element-1678" />)}
                                </div>
                              </div>
                            </motion.div>}
                        </div>

                        {/* Summary Card (after call ends) */}
                        <AnimatePresence data-id="element-1679">
                          {!callIsLive && callStep === 'detail' && visibleMessages.length >= LIVE_CALL_SCRIPT.length && <motion.div initial={{
                      opacity: 0,
                      y: 30,
                      scale: 0.97
                    }} animate={{
                      opacity: 1,
                      y: 0,
                      scale: 1
                    }} transition={{
                      delay: 1,
                      duration: 0.8,
                      ease: [0.16, 1, 0.3, 1]
                    }} className="px-4" data-id="element-1680">
                                <div className="rounded-2xl p-5 bg-[#141414] border border-white/5" data-id="element-1681">
                                  <div className="flex items-center gap-2 mb-3" data-id="element-1682">
                                    <div className="w-7 h-7 rounded-full bg-vexu/15 flex items-center justify-center" data-id="element-1683">
                                      <CheckCircle2 className="w-3.5 h-3.5 text-vexu" data-id="element-1684" />
                                    </div>
                                    <h3 className="text-sm font-bold text-white" data-id="element-1685">
                                      Call Summary
                                    </h3>
                                  </div>
                                  <div className="flex items-center gap-4 mb-3 pb-3 border-b border-white/5" data-id="element-1686">
                                    <div data-id="element-1687">
                                      <p className="text-[10px] uppercase tracking-wider font-medium text-zinc-500" data-id="element-1688">
                                        Duration
                                      </p>
                                      <p className="text-sm font-bold text-white" data-id="element-1689">
                                        2m 32s
                                      </p>
                                    </div>
                                    <div data-id="element-1690">
                                      <p className="text-[10px] uppercase tracking-wider font-medium text-zinc-500" data-id="element-1691">
                                        Group
                                      </p>
                                      <p className="text-sm font-bold text-white" data-id="element-1692">
                                        Work Contacts
                                      </p>
                                    </div>
                                  </div>
                                  <p className="text-xs leading-relaxed mb-3 text-zinc-400" data-id="element-1693">
                                    Rachel from legal called regarding the
                                    Meridian partnership NDA. Sign-off needed by
                                    Friday EOD. Section 4.2 flagged for personal
                                    review. Callback requested. Marked as urgent
                                    priority.
                                  </p>
                                  <div className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06]" data-id="element-1694">
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-white/10 text-zinc-400" data-id="element-1695">
                                      <Play className="w-3.5 h-3.5 ml-0.5" data-id="element-1696" />
                                    </div>
                                    <span className="text-[12px] font-medium text-zinc-300" data-id="element-1697">
                                      Play Full Call
                                    </span>
                                    <span className="ml-auto text-[10px] font-medium text-zinc-500" data-id="element-1698">
                                      2m 32s
                                    </span>
                                  </div>
                                </div>
                              </motion.div>}
                        </AnimatePresence>
                      </div>

                      {/* Command Input Bar */}
                      <div className="absolute bottom-5 left-4 right-4 z-30" data-id="element-1699">
                        <div className="flex gap-2 p-2 rounded-full shadow-lg backdrop-blur-xl bg-[#141414]/80 border border-white/10" data-id="element-1700">
                          <input type="text" readOnly placeholder="Send a command to agent..." className="flex-1 rounded-full px-4 py-2 text-sm outline-none bg-transparent text-white placeholder:text-zinc-500" data-id="element-1701" />
                          <div className="w-9 h-9 rounded-full bg-vexu text-white flex items-center justify-center opacity-40 flex-shrink-0" data-id="element-1702">
                            <ChevronRight className="w-4 h-4" data-id="element-1703" />
                          </div>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>

                {/* Bottom Tab Bar - only on list view */}
                {callStep === 'list' && <div className="absolute bottom-0 left-0 right-0 z-40 flex justify-center pb-5 px-4" data-id="element-1704">
                    <div className="w-full max-w-[290px] rounded-full px-2 py-[3px] backdrop-blur-2xl border bg-[#111111] border-white/[0.08] shadow-[0_-2px_12px_rgba(0,0,0,0.3),0_4px_24px_rgba(0,0,0,0.4)]" data-id="element-1705">
                      <div className="flex items-center justify-around" data-id="element-1706">
                        {[{
                    id: 'home',
                    label: 'Home'
                  }, {
                    id: 'calls',
                    label: 'Calls'
                  }, {
                    id: 'calendar',
                    label: 'Calendar'
                  }, {
                    id: 'profile',
                    label: 'Account'
                  }].map(tab => {
                    const isActive = tab.id === 'calls';
                    return <div key={tab.id} className="relative flex flex-col items-center justify-center w-[50px] py-1 rounded-xl" data-id="element-1707">
                              {isActive && <div className="absolute inset-0 rounded-xl bg-vexu/[0.08]" data-id="element-1708" />}
                              <div className="relative z-10 flex items-center justify-center" data-id="element-1709">
                                {tab.id === 'calls' ? <div className="w-7 h-7 rounded-full flex items-center justify-center bg-vexu/15 text-vexu" data-id="element-1710">
                                    <Phone className="w-[15px] h-[15px]" strokeWidth={2.2} data-id="element-1711" />
                                  </div> : <div data-id="element-1712">
                                    {tab.id === 'home' && <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-500" data-id="element-1713">
                                        <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" data-id="element-1714" />
                                        <polyline points="9 22 9 12 15 12 15 22" data-id="element-1715" />
                                      </svg>}
                                    {tab.id === 'calendar' && <Calendar className="w-[17px] h-[17px] text-zinc-500" strokeWidth={1.6} data-id="element-1716" />}
                                    {tab.id === 'profile' && <User className="w-[17px] h-[17px] text-zinc-500" strokeWidth={1.6} data-id="element-1717" />}
                                  </div>}
                              </div>
                              <span className={`relative z-10 text-[9px] font-medium mt-0.5 tracking-wide ${isActive ? 'text-vexu opacity-100' : 'text-zinc-500 opacity-45'}`} data-id="element-1718">
                                {tab.label}
                              </span>
                            </div>;
                  })}
                      </div>
                    </div>
                  </div>}
              </motion.div>}
          </AnimatePresence>

          {/* Phase 11: Scenarios */}
          <AnimatePresence data-id="element-1719">
            {phase === 11 && <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            filter: 'blur(10px)'
          }} transition={{
            duration: 1,
            ease
          }} className="absolute inset-0 bg-[#0A0A0A] flex flex-col pt-14 px-4 gap-3" data-id="element-1720">
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2,
              duration: 0.8
            }} className="bg-gradient-to-br from-[#141414] to-[#0A0A0A] border border-purple-500/20 rounded-2xl p-4 shadow-lg relative overflow-hidden" data-id="element-1721">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-purple-500/10 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-1722" />
                  <div className="flex items-center gap-2 mb-2" data-id="element-1723">
                    <Briefcase className="w-4 h-4 text-purple-400" data-id="element-1724" />
                    <span className="text-[10px] font-bold text-purple-400 uppercase tracking-wider" data-id="element-1725">
                      Business
                    </span>
                  </div>
                  <p className="text-sm font-bold text-white mb-1" data-id="element-1726">
                    Client Reschedule
                  </p>
                  <p className="text-xs text-zinc-400 mb-3" data-id="element-1727">
                    Meeting moved to Thursday 10 AM.
                  </p>
                  <div className="flex items-center gap-1 text-vexu text-[10px] font-bold uppercase" data-id="element-1728">
                    <CheckCircle2 className="w-3 h-3" data-id="element-1729" /> Handled by AI
                  </div>
                </motion.div>

                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.6,
              duration: 0.8
            }} className="bg-gradient-to-br from-[#141414] to-[#0A0A0A] border border-blue-500/20 rounded-2xl p-4 shadow-lg relative overflow-hidden" data-id="element-1730">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-1731" />
                  <div className="flex items-center gap-2 mb-2" data-id="element-1732">
                    <Heart className="w-4 h-4 text-blue-400" data-id="element-1733" />
                    <span className="text-[10px] font-bold text-blue-400 uppercase tracking-wider" data-id="element-1734">
                      Priority
                    </span>
                  </div>
                  <p className="text-sm font-bold text-white mb-1" data-id="element-1735">Mom</p>
                  <p className="text-xs text-zinc-400 mb-3" data-id="element-1736">
                    "Call me back when you're free, it's about dinner."
                  </p>
                  <div className="flex items-center gap-1 text-blue-400 text-[10px] font-bold uppercase" data-id="element-1737">
                    <Phone className="w-3 h-3" data-id="element-1738" /> Callback Suggested
                  </div>
                </motion.div>

                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1,
              duration: 0.8
            }} className="bg-gradient-to-br from-[#141414] to-[#0A0A0A] border border-red-500/20 rounded-2xl p-4 shadow-lg relative overflow-hidden" data-id="element-1739">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-red-500/10 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-1740" />
                  <div className="flex items-center gap-2 mb-2" data-id="element-1741">
                    <ShieldAlert className="w-4 h-4 text-red-500" data-id="element-1742" />
                    <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider" data-id="element-1743">
                      Spam
                    </span>
                  </div>
                  <p className="text-sm font-bold text-white mb-1" data-id="element-1744">
                    Telemarketer
                  </p>
                  <p className="text-xs text-zinc-400 mb-3" data-id="element-1745">
                    Detected as high-risk spam.
                  </p>
                  <div className="flex items-center gap-1 text-red-500 text-[10px] font-bold uppercase" data-id="element-1746">
                    <Shield className="w-3 h-3" data-id="element-1747" /> Auto-Blocked
                  </div>
                </motion.div>
              </motion.div>}
          </AnimatePresence>

          {/* Phase 15: Phone Number Entry */}
          <AnimatePresence data-id="element-1748">
            {phase === 15 && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0,
            x: -40
          }} transition={{
            duration: 0.8,
            ease
          }} className="absolute inset-0 bg-[#0A0A0A] flex flex-col items-center justify-center px-8" data-id="element-1749">
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3,
              duration: 0.8
            }} className="w-full" data-id="element-1750">
                  <h2 className="text-2xl font-bold text-white mb-2 text-center" data-id="element-1751">
                    Enter Your Number
                  </h2>
                  <p className="text-sm text-zinc-400 mb-8 text-center" data-id="element-1752">
                    We'll send you a verification code
                  </p>

                  <div className="relative" data-id="element-1753">
                    <div className="w-full h-14 rounded-2xl bg-[#141414] border border-white/10 flex items-center justify-center" data-id="element-1754">
                      <span className="text-xl font-medium text-white tracking-wider" data-id="element-1755">
                        {phoneDigits}
                        {phoneDigits.length < 14 && <motion.span animate={{
                      opacity: [1, 0]
                    }} transition={{
                      duration: 0.8,
                      repeat: Infinity
                    }} className="inline-block w-0.5 h-6 bg-vexu ml-1" data-id="element-1756" />}
                      </span>
                    </div>
                  </div>
                </motion.div>
              </motion.div>}
          </AnimatePresence>

          {/* Phase 16: OTP Verification */}
          <AnimatePresence data-id="element-1757">
            {phase === 16 && <motion.div initial={{
            opacity: 0,
            x: 40
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            scale: 1.1,
            filter: 'blur(10px)'
          }} transition={{
            duration: 0.5,
            ease
          }} className="absolute inset-0 bg-[#0A0A0A] flex flex-col items-center justify-center px-8" data-id="element-1758">
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2,
              duration: 0.8
            }} className="w-full" data-id="element-1759">
                  <h2 className="text-2xl font-bold text-white mb-2 text-center" data-id="element-1760">
                    Verify Code
                  </h2>
                  <p className="text-sm text-zinc-400 mb-8 text-center" data-id="element-1761">
                    Sent to (555) 123-4567
                  </p>

                  <div className="flex gap-3 justify-center mb-6" data-id="element-1762">
                    {[0, 1, 2, 3].map(i => <motion.div key={i} initial={{
                  scale: 0.8,
                  opacity: 0
                }} animate={{
                  scale: otpDigits.length > i ? 1 : 0.95,
                  opacity: 1,
                  borderColor: otpDigits.length > i ? 'rgba(0, 200, 83, 0.5)' : 'rgba(255, 255, 255, 0.1)'
                }} transition={{
                  delay: 0.5 + i * 0.1,
                  duration: 0.3
                }} className="w-14 h-16 rounded-xl bg-[#141414] border-2 flex items-center justify-center" data-id="element-1763">
                        <span className="text-2xl font-bold text-white" data-id="element-1764">
                          {otpDigits[i] || ''}
                        </span>
                      </motion.div>)}
                  </div>

                  {otpDigits.length === 4 && <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: 0.3,
                duration: 0.5
              }} className="flex items-center justify-center gap-2 text-vexu" data-id="element-1765">
                      <CheckCircle2 className="w-5 h-5" data-id="element-1766" />
                      <span className="text-sm font-semibold" data-id="element-1767">Verified</span>
                    </motion.div>}
                </motion.div>
              </motion.div>}
          </AnimatePresence>

          {/* Phase 17: Success Transition */}
          <AnimatePresence data-id="element-1768">
            {phase === 17 && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.8
          }} className="absolute inset-0 bg-[#0A0A0A] flex items-center justify-center" data-id="element-1769">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: [0, 1.2, 1]
            }} transition={{
              duration: 1,
              ease
            }} className="w-24 h-24 rounded-full bg-vexu/20 border-4 border-vexu flex items-center justify-center" data-id="element-1770">
                  <CheckCircle2 className="w-12 h-12 text-vexu" data-id="element-1771" />
                </motion.div>
              </motion.div>}
          </AnimatePresence>

          {/* Phase 18-19: Home Dashboard with Activation Settings */}
          <AnimatePresence data-id="element-1772">
            {(phase === 18 || phase === 19) && <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            filter: 'blur(10px)'
          }} transition={{
            duration: 0.8,
            ease
          }} className="absolute inset-0 bg-[#0A0A0A] flex flex-col" data-id="element-1773">
                {/* Header */}
                <div className="pt-14 pb-4 px-6 flex flex-col items-center border-b border-white/5" data-id="element-1774">
                  <img src="https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg" alt="Vexu" className="w-10 h-10 mb-2" data-id="element-1775" />
                  <h2 className="text-lg font-bold text-white" data-id="element-1776">
                    Welcome to Vexu
                  </h2>
                  <p className="text-xs text-zinc-500" data-id="element-1777">
                    Your AI call assistant
                  </p>
                </div>

                {/* Activation Card */}
                <div className="flex-1 p-5 overflow-hidden" data-id="element-1778">
                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.5,
                duration: 0.8
              }} data-id="element-1779">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3" data-id="element-1780">
                      Activation Settings
                    </h3>

                    <div className="bg-[#111111]/80 border border-white/5 rounded-3xl overflow-hidden backdrop-blur-xl" data-id="element-1781">
                      <div className="p-5" data-id="element-1782">
                        <div className="flex items-center gap-3 mb-4" data-id="element-1783">
                          <div className="w-10 h-10 rounded-full bg-vexu/10 flex items-center justify-center" data-id="element-1784">
                            <Shield className="w-5 h-5 text-vexu" data-id="element-1785" />
                          </div>
                          <div data-id="element-1786">
                            <p className="text-sm font-bold text-white" data-id="element-1787">
                              {activatedScenario ? '1' : '0'} of 4 Active
                            </p>
                            <p className="text-xs text-zinc-400" data-id="element-1788">
                              Choose when AI answers
                            </p>
                          </div>
                        </div>

                        {/* Scenarios */}
                        <div className="space-y-0 border-t border-white/5" data-id="element-1789">
                          {[{
                        id: 'busy',
                        label: 'When Busy',
                        icon: Phone
                      }, {
                        id: 'no-answer',
                        label: 'No Answer',
                        icon: PhoneOff
                      }, {
                        id: 'offline',
                        label: 'Phone Offline',
                        icon: WifiOff
                      }, {
                        id: 'unreachable',
                        label: 'Unreachable',
                        icon: AlertTriangle
                      }].map((scenario, i) => {
                        const Icon = scenario.icon;
                        const isActive = activatedScenario === scenario.id;
                        return <motion.div key={scenario.id} initial={{
                          opacity: 0,
                          x: -10
                        }} animate={{
                          opacity: 1,
                          x: 0
                        }} transition={{
                          delay: 0.8 + i * 0.1,
                          duration: 0.5
                        }} className={`flex items-center justify-between p-4 ${i !== 3 ? 'border-b border-white/5' : ''}`} data-id="element-1790">
                                <div className="flex items-center gap-3" data-id="element-1791">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isActive ? 'bg-vexu/10' : 'bg-white/5'}`} data-id="element-1792">
                                    <Icon className={`w-4 h-4 transition-colors ${isActive ? 'text-vexu' : 'text-zinc-500'}`} data-id="element-1793" />
                                  </div>
                                  <span className="text-sm font-semibold text-white" data-id="element-1794">
                                    {scenario.label}
                                  </span>
                                </div>

                                <motion.button animate={{
                            backgroundColor: isActive ? 'rgba(0, 200, 83, 1)' : 'rgba(255, 255, 255, 0.1)'
                          }} transition={{
                            duration: 0.3
                          }} className="relative w-11 h-6 rounded-full" data-id="element-1795">
                                  <motion.div animate={{
                              x: isActive ? 22 : 2
                            }} transition={{
                              type: 'spring',
                              stiffness: 500,
                              damping: 30
                            }} className="absolute top-1 w-4 h-4 rounded-full bg-white shadow-lg" data-id="element-1796" />
                                  {isActive && <motion.div initial={{
                              scale: 0,
                              opacity: 0
                            }} animate={{
                              scale: 1,
                              opacity: 1
                            }} transition={{
                              delay: 0.2,
                              duration: 0.3
                            }} className="absolute inset-0 flex items-center justify-center" data-id="element-1797">
                                      <CheckCircle2 className="w-3 h-3 text-white" data-id="element-1798" />
                                    </motion.div>}
                                </motion.button>
                              </motion.div>;
                      })}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* Phase 21-25: Caller Experience Scene */}
          <AnimatePresence data-id="element-1799">
            {phase >= 21 && phase <= 25 && <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            filter: 'blur(10px)'
          }} transition={{
            duration: 0.8,
            ease
          }} className="absolute inset-0 bg-[#0A0A0A] flex flex-col" data-id="element-1800">
                {/* Calls Page View (Phase 21 intro) */}
                <AnimatePresence mode="wait" data-id="element-1801">
                  {ceStep === 'callsPage' && <motion.div key="ce-calls-page" initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -40
              }} transition={{
                duration: 0.4,
                ease
              }} className="absolute inset-0 flex flex-col" data-id="element-1802">
                      <div className="px-5 pt-14 pb-3 bg-[#0A0A0A]" data-id="element-1803">
                        <div className="flex items-center justify-between mb-3" data-id="element-1804">
                          <h1 className="text-2xl font-bold text-white" data-id="element-1805">
                            Calls
                          </h1>
                          <motion.button animate={ceButtonPressed ? {
                      scale: 0.92,
                      backgroundColor: 'rgba(0,200,83,0.9)',
                      color: '#000',
                      boxShadow: '0 0 30px rgba(0,200,83,0.6)',
                      borderColor: 'rgba(0,200,83,0.8)'
                    } : {
                      boxShadow: ['0 0 0px rgba(0,200,83,0)', '0 0 24px rgba(0,200,83,0.5)', '0 0 0px rgba(0,200,83,0)'],
                      scale: [1, 1.08, 1]
                    }} transition={ceButtonPressed ? {
                      duration: 0.2,
                      ease: [0.16, 1, 0.3, 1]
                    } : {
                      duration: 1.5,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }} className="px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 bg-vexu/10 text-vexu border border-vexu/20" data-id="element-1806">
                            <Users className="w-3.5 h-3.5" data-id="element-1807" />
                            Caller Experience
                          </motion.button>
                        </div>
                        <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-[#141414] border border-white/[0.06]" data-id="element-1808">
                          <Search className="w-4 h-4 flex-shrink-0 text-zinc-500" data-id="element-1809" />
                          <span className="text-[13px] text-zinc-500" data-id="element-1810">
                            Search calls...
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 overflow-hidden" data-id="element-1811">
                        {/* Legal Department */}
                        <div className="w-full px-5 py-3.5 flex items-center gap-3.5 border-b border-white/[0.04]" data-id="element-1812">
                          <div className="w-11 h-11 rounded-full flex items-center justify-center font-semibold text-[13px] bg-amber-100/60 text-amber-600" data-id="element-1813">
                            LD
                          </div>
                          <div className="flex-1 text-left min-w-0" data-id="element-1814">
                            <span className="text-[14px] font-semibold text-white" data-id="element-1815">
                              Legal Department
                            </span>
                            <div className="flex items-center gap-1.5 text-[12px] mt-0.5 text-zinc-500" data-id="element-1816">
                              <PhoneIncoming className="w-3 h-3 text-zinc-500" data-id="element-1817" />
                              <span data-id="element-1818">Work Contacts</span>
                              <span className="text-zinc-600" data-id="element-1819">·</span>
                              <span data-id="element-1820">2m 34s</span>
                            </div>
                          </div>
                          <div className="text-[11px] font-medium text-zinc-500" data-id="element-1821">
                            2:34 PM
                          </div>
                        </div>
                        {/* Mike Ross */}
                        <div className="w-full px-5 py-3.5 flex items-center gap-3.5 border-b border-white/[0.04]" data-id="element-1822">
                          <div className="w-11 h-11 rounded-full flex items-center justify-center font-semibold text-[13px] bg-blue-100/60 text-blue-600" data-id="element-1823">
                            MR
                          </div>
                          <div className="flex-1 text-left min-w-0" data-id="element-1824">
                            <span className="text-[14px] font-semibold text-white" data-id="element-1825">
                              Mike Ross
                            </span>
                            <div className="flex items-center gap-1.5 text-[12px] mt-0.5 text-zinc-500" data-id="element-1826">
                              <PhoneIncoming className="w-3 h-3 text-zinc-500" data-id="element-1827" />
                              <span data-id="element-1828">VIP Clients</span>
                              <span className="text-zinc-600" data-id="element-1829">·</span>
                              <span data-id="element-1830">2m 14s</span>
                            </div>
                          </div>
                          <div className="text-[11px] font-medium text-zinc-500" data-id="element-1831">
                            11:15 AM
                          </div>
                        </div>
                        {/* Unknown */}
                        <div className="w-full px-5 py-3.5 flex items-center gap-3.5 border-b border-white/[0.04]" data-id="element-1832">
                          <div className="w-11 h-11 rounded-full flex items-center justify-center font-semibold text-[13px] bg-slate-100/60 text-slate-600" data-id="element-1833">
                            #
                          </div>
                          <div className="flex-1 text-left min-w-0" data-id="element-1834">
                            <span className="text-[14px] font-semibold text-white" data-id="element-1835">
                              +1 (555) 012-3456
                            </span>
                            <div className="flex items-center gap-1.5 text-[12px] mt-0.5 text-zinc-500" data-id="element-1836">
                              <PhoneOff className="w-3 h-3 text-zinc-500" data-id="element-1837" />
                              <span data-id="element-1838">All Contacts</span>
                              <span className="text-zinc-600" data-id="element-1839">·</span>
                              <span data-id="element-1840">45s</span>
                            </div>
                          </div>
                          <div className="text-[11px] font-medium text-zinc-500" data-id="element-1841">
                            Yesterday
                          </div>
                        </div>
                        {/* Design Team */}
                        <div className="w-full px-5 py-3.5 flex items-center gap-3.5 border-b border-white/[0.04]" data-id="element-1842">
                          <div className="w-11 h-11 rounded-full flex items-center justify-center font-semibold text-[13px] bg-emerald-100/60 text-emerald-600" data-id="element-1843">
                            DT
                          </div>
                          <div className="flex-1 text-left min-w-0" data-id="element-1844">
                            <span className="text-[14px] font-semibold text-white" data-id="element-1845">
                              Design Team
                            </span>
                            <div className="flex items-center gap-1.5 text-[12px] mt-0.5 text-zinc-500" data-id="element-1846">
                              <PhoneIncoming className="w-3 h-3 text-zinc-500" data-id="element-1847" />
                              <span data-id="element-1848">Work Contacts</span>
                              <span className="text-zinc-600" data-id="element-1849">·</span>
                              <span data-id="element-1850">5m 30s</span>
                            </div>
                          </div>
                          <div className="text-[11px] font-medium text-zinc-500" data-id="element-1851">
                            Mon
                          </div>
                        </div>
                      </div>
                      {/* Bottom Tab Bar */}
                      <div className="absolute bottom-0 left-0 right-0 z-40 flex justify-center pb-5 px-4" data-id="element-1852">
                        <div className="w-full max-w-[290px] rounded-full px-2 py-[3px] backdrop-blur-2xl border bg-[#111111] border-white/[0.08] shadow-[0_-2px_12px_rgba(0,0,0,0.3),0_4px_24px_rgba(0,0,0,0.4)]" data-id="element-1853">
                          <div className="flex items-center justify-around" data-id="element-1854">
                            {[{
                        id: 'home',
                        label: 'Home',
                        icon: 'home'
                      }, {
                        id: 'calls',
                        label: 'Calls',
                        icon: 'calls'
                      }, {
                        id: 'calendar',
                        label: 'Calendar',
                        icon: 'calendar'
                      }, {
                        id: 'profile',
                        label: 'Account',
                        icon: 'profile'
                      }].map(tab => {
                        const isActive = tab.id === 'calls';
                        return <div key={tab.id} className="relative flex flex-col items-center justify-center w-[50px] py-1 rounded-xl" data-id="element-1855">
                                  {isActive && <div className="absolute inset-0 rounded-xl bg-vexu/[0.08]" data-id="element-1856" />}
                                  <div className="relative z-10 flex items-center justify-center" data-id="element-1857">
                                    {tab.id === 'calls' ? <div className="w-7 h-7 rounded-full flex items-center justify-center bg-vexu/15 text-vexu" data-id="element-1858">
                                        <Phone className="w-[15px] h-[15px]" strokeWidth={2.2} data-id="element-1859" />
                                      </div> : <div data-id="element-1860">
                                        {tab.id === 'home' && <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-500" data-id="element-1861">
                                            <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" data-id="element-1862" />
                                            <polyline points="9 22 9 12 15 12 15 22" data-id="element-1863" />
                                          </svg>}
                                        {tab.id === 'calendar' && <Calendar className="w-[17px] h-[17px] text-zinc-500" strokeWidth={1.6} data-id="element-1864" />}
                                        {tab.id === 'profile' && <User className="w-[17px] h-[17px] text-zinc-500" strokeWidth={1.6} data-id="element-1865" />}
                                      </div>}
                                  </div>
                                  <span className={`relative z-10 text-[9px] font-medium mt-0.5 tracking-wide ${isActive ? 'text-vexu opacity-100' : 'text-zinc-500 opacity-45'}`} data-id="element-1866">
                                    {tab.label}
                                  </span>
                                </div>;
                      })}
                          </div>
                        </div>
                      </div>
                    </motion.div>}

                  {/* Dashboard View (Phases 21, 22, 25) */}
                  {(ceStep === 'dashboard' || ceStep === 'addModal' || ceStep === 'groups') && <motion.div key="ce-dashboard" initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="absolute inset-0 flex flex-col" data-id="element-1867">
                      {/* Header */}
                      <div className="px-6 pt-14 pb-4 flex items-center gap-3 border-b border-white/5" data-id="element-1868">
                        <div className="p-2 -ml-2 rounded-full border text-zinc-400 bg-white/5 backdrop-blur-md border-white/10" data-id="element-1869">
                          <ArrowLeft className="w-5 h-5" data-id="element-1870" />
                        </div>
                        <div className="flex-1" data-id="element-1871">
                          <h1 className="text-lg font-bold text-white" data-id="element-1872">
                            Caller Experience
                          </h1>
                          <p className="text-xs text-zinc-400" data-id="element-1873">
                            Configure how AI handles different callers
                          </p>
                        </div>
                        <motion.div animate={cePlusPressed ? {
                    scale: 0.85,
                    backgroundColor: 'rgba(0,200,83,0.9)',
                    color: '#000',
                    boxShadow: '0 0 30px rgba(0,200,83,0.6)',
                    borderColor: 'rgba(0,200,83,0.8)'
                  } : phase === 21 ? {
                    boxShadow: ['0 0 0px rgba(0,200,83,0)', '0 0 20px rgba(0,200,83,0.4)', '0 0 0px rgba(0,200,83,0)'],
                    scale: [1, 1.1, 1]
                  } : {
                    boxShadow: 'none',
                    scale: 1
                  }} transition={cePlusPressed ? {
                    duration: 0.2,
                    ease: [0.16, 1, 0.3, 1]
                  } : {
                    duration: 2,
                    repeat: Infinity
                  }} className="p-2 rounded-full border text-vexu bg-vexu/10 border-vexu/20" data-id="element-1874">
                          <Plus className="w-5 h-5" data-id="element-1875" />
                        </motion.div>
                      </div>

                      {/* Groups List */}
                      <div className="flex-1 p-6 space-y-3" data-id="element-1876">
                        {/* All Callers / Everyone Else */}
                        <motion.div layout className="rounded-2xl overflow-hidden bg-[#111111]/80 border border-white/5" data-id="element-1877">
                          <div className="w-full flex items-center gap-3 p-4" data-id="element-1878">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-vexu/10" data-id="element-1879">
                              <Users className="w-5 h-5 text-vexu" data-id="element-1880" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-1881">
                              <div className="flex items-center gap-2" data-id="element-1882">
                                <span className="text-sm font-bold text-white" data-id="element-1883">
                                  {ceStep === 'groups' ? 'Everyone Else' : 'All Callers'}
                                </span>
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium bg-blue-500/10 text-blue-400" data-id="element-1884">
                                  Default
                                </span>
                              </div>
                              <span className="text-[11px] text-zinc-400" data-id="element-1885">
                                {ceStep === 'groups' ? 'All other callers → default handling' : 'Handles all incoming calls'}{' '}
                                · Default AI Voice
                              </span>
                            </div>
                            <ChevronDown className="w-4 h-4 text-zinc-600" data-id="element-1886" />
                          </div>
                        </motion.div>

                        {/* Additional Groups (Phase 25) */}
                        <AnimatePresence data-id="element-1887">
                          {ceStep === 'groups' && <>
                              <motion.div initial={{
                        opacity: 0,
                        y: 20
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} transition={{
                        delay: 0.2
                      }} className="rounded-2xl overflow-hidden bg-[#111111]/80 border border-white/5" data-id="element-1888">
                                <div className="w-full flex items-center gap-3 p-4" data-id="element-1889">
                                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-pink-500/10" data-id="element-1890">
                                    <Heart className="w-5 h-5 text-pink-500" data-id="element-1891" />
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-1892">
                                    <span className="text-sm font-bold text-white" data-id="element-1893">
                                      Family
                                    </span>
                                    <span className="text-[11px] text-zinc-400 block" data-id="element-1894">
                                      4 contacts → custom handling · Ruby
                                      Dialect
                                    </span>
                                  </div>
                                  <ChevronDown className="w-4 h-4 text-zinc-600" data-id="element-1895" />
                                </div>
                              </motion.div>

                              <motion.div initial={{
                        opacity: 0,
                        y: 20
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} transition={{
                        delay: 0.4
                      }} className="rounded-2xl overflow-hidden bg-[#111111]/80 border border-white/5" data-id="element-1896">
                                <div className="w-full flex items-center gap-3 p-4" data-id="element-1897">
                                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-purple-500/10" data-id="element-1898">
                                    <Briefcase className="w-5 h-5 text-purple-500" data-id="element-1899" />
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-1900">
                                    <span className="text-sm font-bold text-white" data-id="element-1901">
                                      Work
                                    </span>
                                    <span className="text-[11px] text-zinc-400 block" data-id="element-1902">
                                      12 contacts → custom handling · Formal
                                      Arabic
                                    </span>
                                  </div>
                                  <ChevronDown className="w-4 h-4 text-zinc-600" data-id="element-1903" />
                                </div>
                              </motion.div>

                              <motion.div initial={{
                        opacity: 0,
                        y: 20
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} transition={{
                        delay: 0.6
                      }} className="rounded-2xl overflow-hidden bg-[#111111]/80 border border-white/5" data-id="element-1904">
                                <div className="w-full flex items-center gap-3 p-4" data-id="element-1905">
                                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-amber-500/10" data-id="element-1906">
                                    <Zap className="w-5 h-5 text-amber-500" data-id="element-1907" />
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-1908">
                                    <span className="text-sm font-bold text-white" data-id="element-1909">
                                      VIP Clients
                                    </span>
                                    <span className="text-[11px] text-zinc-400 block" data-id="element-1910">
                                      8 contacts → custom handling · Premium
                                      English
                                    </span>
                                  </div>
                                  <ChevronDown className="w-4 h-4 text-zinc-600" data-id="element-1911" />
                                </div>
                              </motion.div>
                            </>}
                        </AnimatePresence>
                      </div>

                      {/* Add Group Modal (Phase 22) */}
                      <AnimatePresence data-id="element-1912">
                        {ceStep === 'addModal' && <motion.div initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} className="absolute inset-0 z-50 flex items-end justify-center p-4 bg-black/40 backdrop-blur-sm" data-id="element-1913">
                            <motion.div initial={{
                      y: 200,
                      opacity: 0
                    }} animate={{
                      y: 0,
                      opacity: 1
                    }} className="relative w-full rounded-3xl overflow-hidden bg-[#1a1a1a] border border-white/10 shadow-2xl p-5" data-id="element-1914">
                              <h3 className="text-base font-bold text-white mb-4" data-id="element-1915">
                                New Group
                              </h3>
                              <div className="relative mb-6" data-id="element-1916">
                                <div className="w-full h-12 rounded-xl bg-white/5 border border-white/10 flex items-center px-4" data-id="element-1917">
                                  <span className="text-white" data-id="element-1918">
                                    {familyGroupName}
                                    {familyGroupName.length < 6 && <motion.span animate={{
                              opacity: [1, 0]
                            }} transition={{
                              duration: 0.8,
                              repeat: Infinity
                            }} className="inline-block w-0.5 h-5 bg-vexu ml-1 align-middle" data-id="element-1919" />}
                                  </span>
                                </div>
                              </div>
                              <motion.button animate={{
                        backgroundColor: familyGroupName === 'Family' ? 'rgba(0,200,83,1)' : 'rgba(255,255,255,0.1)',
                        color: familyGroupName === 'Family' ? '#000' : '#fff'
                      }} className="w-full py-3 rounded-xl font-bold text-sm" data-id="element-1920">
                                Create Group
                              </motion.button>
                            </motion.div>
                          </motion.div>}
                      </AnimatePresence>
                    </motion.div>}

                  {/* Settings View (Phases 23-24) */}
                  {(ceStep === 'settings' || ceStep === 'interactions') && <motion.div key="ce-settings" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: 20
              }} className="absolute inset-0 flex flex-col bg-[#0A0A0A]" data-id="element-1921">
                      {/* Header */}
                      <div className="px-6 pt-14 pb-4 flex items-center gap-3 border-b border-white/5" data-id="element-1922">
                        <div className="p-2 -ml-2 rounded-full border text-zinc-400 bg-white/5 backdrop-blur-md border-white/10" data-id="element-1923">
                          <ArrowLeft className="w-5 h-5" data-id="element-1924" />
                        </div>
                        <div className="flex-1 flex items-center gap-2" data-id="element-1925">
                          <div className="w-8 h-8 rounded-lg bg-pink-500/10 flex items-center justify-center" data-id="element-1926">
                            <Heart className="w-4 h-4 text-pink-500" data-id="element-1927" />
                          </div>
                          <h1 className="text-lg font-bold text-white" data-id="element-1928">
                            Family
                          </h1>
                        </div>
                      </div>

                      <div className="flex-1 p-4 overflow-hidden space-y-3" data-id="element-1929">
                        {/* Core Settings */}
                        <div className="rounded-xl overflow-hidden bg-white/[0.02]" data-id="element-1930">
                          {[{
                      id: 0,
                      icon: Globe,
                      label: 'Dialect',
                      value: 'Ruby'
                    }, {
                      id: 1,
                      icon: Mic,
                      label: 'Tone',
                      value: 'Warm & Friendly'
                    }, {
                      id: 2,
                      icon: Globe,
                      label: 'Languages',
                      value: 'Arabic, English'
                    }].map((row, i) => <motion.div key={row.id} animate={{
                      backgroundColor: highlightedFeature === row.id ? 'rgba(0,200,83,0.1)' : 'transparent',
                      borderColor: highlightedFeature === row.id ? 'rgba(0,200,83,0.5)' : 'rgba(255,255,255,0.04)'
                    }} className={`w-full px-3.5 py-3 flex items-center justify-between border-b ${i === 2 ? 'border-transparent' : ''}`} data-id="element-1931">
                              <div className="flex items-center gap-2.5" data-id="element-1932">
                                <row.icon className={`w-4 h-4 ${highlightedFeature === row.id ? 'text-vexu' : 'text-zinc-500'}`} data-id="element-1933" />
                                <span className="text-sm font-medium text-zinc-300" data-id="element-1934">
                                  {row.label}
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5" data-id="element-1935">
                                <span className={`text-xs font-medium ${highlightedFeature === row.id ? 'text-vexu' : 'text-vexu'}`} data-id="element-1936">
                                  {row.value}
                                </span>
                                <ChevronRight className="w-3.5 h-3.5 text-zinc-600" data-id="element-1937" />
                              </div>
                            </motion.div>)}
                        </div>

                        {/* Features */}
                        <div className="rounded-xl overflow-hidden bg-white/[0.02]" data-id="element-1938">
                          {/* Calendar */}
                          <motion.div animate={{
                      backgroundColor: highlightedFeature === 3 ? 'rgba(0,200,83,0.1)' : 'transparent',
                      borderColor: highlightedFeature === 3 ? 'rgba(0,200,83,0.5)' : 'rgba(255,255,255,0.04)'
                    }} className="w-full px-3.5 py-3 flex items-center justify-between border-b" data-id="element-1939">
                            <div className="flex items-center gap-2.5" data-id="element-1940">
                              <Calendar className={`w-4 h-4 ${highlightedFeature === 3 ? 'text-vexu' : 'text-zinc-500'}`} data-id="element-1941" />
                              <span className="text-sm font-medium text-zinc-300" data-id="element-1942">
                                Calendar Access
                              </span>
                            </div>
                            <div className={`w-9 h-5 rounded-full flex items-center px-0.5 transition-colors ${ceStep === 'interactions' ? 'bg-vexu' : 'bg-white/10'}`} data-id="element-1943">
                              <motion.div animate={{
                          x: ceStep === 'interactions' ? 16 : 0
                        }} transition={{
                          type: 'spring',
                          stiffness: 500,
                          damping: 30
                        }} className="w-4 h-4 rounded-full bg-white shadow-sm" data-id="element-1944" />
                            </div>
                          </motion.div>

                          {/* Contacts */}
                          <motion.div animate={{
                      backgroundColor: highlightedFeature === 4 ? 'rgba(0,200,83,0.1)' : 'transparent',
                      borderColor: highlightedFeature === 4 ? 'rgba(0,200,83,0.5)' : 'rgba(255,255,255,0.04)'
                    }} className="w-full px-3.5 py-3 flex items-center justify-between border-b" data-id="element-1945">
                            <div className="flex items-center gap-2.5" data-id="element-1946">
                              <Users className={`w-4 h-4 ${highlightedFeature === 4 ? 'text-vexu' : 'text-zinc-500'}`} data-id="element-1947" />
                              <span className="text-sm font-medium text-zinc-300" data-id="element-1948">
                                Contacts
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5" data-id="element-1949">
                              <span className="text-xs font-medium text-zinc-400" data-id="element-1950">
                                {ceStep === 'interactions' ? '4 assigned' : '0 assigned'}
                              </span>
                              <ChevronRight className="w-3.5 h-3.5 text-zinc-600" data-id="element-1951" />
                            </div>
                          </motion.div>
                        </div>

                        {/* Prompts */}
                        <div className="rounded-xl overflow-hidden bg-white/[0.02]" data-id="element-1952">
                          {/* Greeting */}
                          <motion.div animate={{
                      backgroundColor: highlightedFeature === 5 ? 'rgba(0,200,83,0.1)' : 'transparent',
                      borderColor: highlightedFeature === 5 ? 'rgba(0,200,83,0.5)' : 'rgba(255,255,255,0.04)'
                    }} className="w-full px-3.5 py-3 flex items-center justify-between border-b" data-id="element-1953">
                            <div className="flex items-center gap-2.5 flex-1 min-w-0" data-id="element-1954">
                              <MessageSquare className={`w-4 h-4 flex-shrink-0 ${highlightedFeature === 5 ? 'text-vexu' : 'text-zinc-500'}`} data-id="element-1955" />
                              <div className="min-w-0" data-id="element-1956">
                                <span className="text-sm font-medium block text-zinc-300" data-id="element-1957">
                                  Greeting
                                </span>
                                <span className="text-[11px] block truncate text-zinc-500" data-id="element-1958">
                                  {ceStep === 'interactions' ? 'Ahlan! This is the family line...' : 'Tap to set...'}
                                </span>
                              </div>
                            </div>
                            <ChevronRight className="w-3.5 h-3.5 text-zinc-600 flex-shrink-0" data-id="element-1959" />
                          </motion.div>

                          {/* Instructions */}
                          <motion.div animate={{
                      backgroundColor: highlightedFeature === 6 ? 'rgba(0,200,83,0.1)' : 'transparent',
                      borderColor: highlightedFeature === 6 ? 'rgba(0,200,83,0.5)' : 'rgba(255,255,255,0.04)'
                    }} className="w-full px-3.5 py-3 flex items-center justify-between" data-id="element-1960">
                            <div className="flex items-center gap-2.5 flex-1 min-w-0" data-id="element-1961">
                              <Brain className={`w-4 h-4 flex-shrink-0 ${highlightedFeature === 6 ? 'text-vexu' : 'text-zinc-500'}`} data-id="element-1962" />
                              <div className="min-w-0" data-id="element-1963">
                                <span className="text-sm font-medium block text-zinc-300" data-id="element-1964">
                                  AI Instructions
                                </span>
                                {ceStep === 'interactions' ? <motion.span animate={{
                            color: ['rgb(113,113,122)', 'rgb(239,68,68)', 'rgb(249,115,22)', 'rgb(239,68,68)', 'rgb(113,113,122)'],
                            textShadow: ['0 0 0px rgba(239,68,68,0)', '0 0 8px rgba(239,68,68,0.6)', '0 0 12px rgba(249,115,22,0.5)', '0 0 8px rgba(239,68,68,0.6)', '0 0 0px rgba(239,68,68,0)']
                          }} transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: 'easeInOut'
                          }} className="text-[11px] block truncate" data-id="element-1965">
                                    Always interrupt for family emergencies...
                                  </motion.span> : <span className="text-[11px] block truncate text-zinc-500" data-id="element-1966">
                                    Tap to set...
                                  </span>}
                              </div>
                            </div>
                            <ChevronRight className="w-3.5 h-3.5 text-zinc-600 flex-shrink-0" data-id="element-1967" />
                          </motion.div>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>}
          </AnimatePresence>

          {/* Phase 28-31: Calendar & Agentic AI Scene */}
          <AnimatePresence data-id="element-1968">
            {phase >= 28 && phase <= 31 && <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            filter: 'blur(10px)'
          }} transition={{
            duration: 0.8,
            ease
          }} className="absolute inset-0 bg-[#0A0A0A] flex flex-col" data-id="element-1969">
                {/* Chat View (Phase 28) */}
                <AnimatePresence mode="wait" data-id="element-1970">
                  {calStep === 'chat' && <motion.div key="cal-chat" initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="absolute inset-0 flex flex-col" data-id="element-1971">
                      {/* Header */}
                      <div className="px-4 pt-14 pb-3 flex items-center gap-3 bg-[#141414] border-b border-white/5" data-id="element-1972">
                        <div className="p-1 rounded-full text-zinc-400" data-id="element-1973">
                          <ArrowLeft className="w-5 h-5" data-id="element-1974" />
                        </div>
                        <div className="relative" data-id="element-1975">
                          <div className="w-9 h-9 rounded-full flex items-center justify-center bg-blue-100/60 text-blue-600" data-id="element-1976">
                            <span className="text-xs font-bold" data-id="element-1977">C</span>
                          </div>
                          <div className="absolute -top-0.5 -right-0.5" data-id="element-1978">
                            <span className="inline-flex rounded-full h-2.5 w-2.5 bg-vexu border-2 border-[#141414]" data-id="element-1979" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-1980">
                          <div className="flex items-center gap-2" data-id="element-1981">
                            <h2 className="text-sm font-bold truncate text-white" data-id="element-1982">
                              Client
                            </h2>
                            <span className="text-[9px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-md bg-vexu/10 text-vexu border border-vexu/20" data-id="element-1983">
                              Live
                            </span>
                          </div>
                          <p className="text-[11px] text-vexu" data-id="element-1984">
                            Live · AI Scheduling
                          </p>
                        </div>
                      </div>

                      {/* Live Call Banner */}
                      <div className="px-4 py-2.5 flex items-center justify-between bg-vexu/[0.03] border-b border-vexu/10" data-id="element-1985">
                        <div className="flex items-center gap-2" data-id="element-1986">
                          <div className="flex items-center gap-1.5" data-id="element-1987">
                            <span className="w-1.5 h-1.5 rounded-full bg-vexu" data-id="element-1988" />
                            <span className="text-[10px] font-semibold uppercase tracking-widest text-vexu" data-id="element-1989">
                              Active Call
                            </span>
                          </div>
                          <span className="text-[10px] text-zinc-500" data-id="element-1990">·</span>
                          <span className="text-[10px] text-zinc-400" data-id="element-1991">
                            AI Agent handling
                          </span>
                        </div>
                        <div className="flex items-center gap-0.5" data-id="element-1992">
                          {[0, 1, 2, 3].map(i => <motion.div key={i} animate={{
                      height: ['3px', '10px', '5px', '8px', '3px']
                    }} transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      delay: i * 0.15
                    }} className="w-[2px] rounded-full bg-vexu/60" data-id="element-1993" />)}
                        </div>
                      </div>

                      {/* Chat Messages */}
                      <div className="flex-1 overflow-y-auto flex flex-col justify-end pb-20" data-id="element-1994">
                        <div className="px-5 space-y-1.5 pb-4" data-id="element-1995">
                          {calChatMessages.map((msg, idx) => <motion.div key={idx} initial={{
                      opacity: 0,
                      y: 6
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-1996">
                              <div className={`max-w-[75%] rounded-[14px] px-3 py-2 ${msg.sender === 'user' ? 'bg-vexu text-white rounded-tr-[4px]' : 'bg-[#1A1A1A] text-zinc-100 rounded-tl-[4px] border border-white/[0.04]'}`} data-id="element-1997">
                                <p className="text-[12px] leading-[1.45] tracking-[-0.01em]" data-id="element-1998">
                                  {msg.text}
                                </p>
                                <div className={`flex items-center justify-end gap-1 mt-0.5 ${msg.sender === 'user' ? 'text-white/40' : 'text-zinc-600'}`} data-id="element-1999">
                                  <span className="text-[9px] tracking-wide" data-id="element-2000">
                                    {msg.time}
                                  </span>
                                </div>
                              </div>
                            </motion.div>)}

                          {isCalTyping && <motion.div initial={{
                      opacity: 0,
                      y: 4
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} className="flex justify-start" data-id="element-2001">
                              <div className="rounded-[14px] rounded-tl-[4px] px-3.5 py-2.5 bg-[#1A1A1A] border border-white/[0.04]" data-id="element-2002">
                                <div className="flex gap-[3px] items-center" data-id="element-2003">
                                  {[0, 0.15, 0.3].map((delay, i) => <motion.div key={i} animate={{
                            opacity: [0.3, 0.8, 0.3]
                          }} transition={{
                            duration: 0.9,
                            repeat: Infinity,
                            delay
                          }} className="w-[5px] h-[5px] rounded-full bg-zinc-600" data-id="element-2004" />)}
                                </div>
                              </div>
                            </motion.div>}
                        </div>
                      </div>
                    </motion.div>}

                  {/* Calendar View (Phases 29-30) */}
                  {(calStep === 'calendar' || calStep === 'features') && <motion.div key="cal-day" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: 20
              }} className="absolute inset-0 flex flex-col bg-[#0A0A0A]" data-id="element-2005">
                      {/* Header */}
                      <div className="px-6 pt-14 pb-2" data-id="element-2006">
                        <div className="flex justify-between items-center mb-5" data-id="element-2007">
                          <h1 className="text-2xl font-bold tracking-tight text-white" data-id="element-2008">
                            Calendar
                          </h1>
                          <div className="w-9 h-9 rounded-full border flex items-center justify-center bg-white/[0.04] border-white/[0.08]" data-id="element-2009">
                            <Settings className="w-4 h-4 text-zinc-400" data-id="element-2010" />
                          </div>
                        </div>

                        {/* View toggle */}
                        <div className="flex p-1 border rounded-xl mb-5 bg-white/[0.04] border-white/[0.06]" data-id="element-2011">
                          <button className="relative flex-1 py-1.5 text-sm font-medium rounded-lg capitalize text-white" data-id="element-2012">
                            <div className="absolute inset-0 rounded-lg border bg-white/[0.08] border-white/[0.06]" data-id="element-2013" />
                            <span className="relative z-10" data-id="element-2014">day</span>
                          </button>
                          <button className="relative flex-1 py-1.5 text-sm font-medium rounded-lg capitalize text-zinc-500" data-id="element-2015">
                            <span className="relative z-10" data-id="element-2016">month</span>
                          </button>
                        </div>

                        {/* Date nav */}
                        <div className="flex items-center justify-between mb-4" data-id="element-2017">
                          <h2 className="text-base font-bold text-white" data-id="element-2018">
                            Tomorrow, Feb 25
                          </h2>
                          <div className="flex gap-1.5" data-id="element-2019">
                            <div className="p-1.5 rounded-lg border bg-white/[0.04] border-white/[0.06]" data-id="element-2020">
                              <ChevronLeft className="w-4 h-4 text-zinc-400" data-id="element-2021" />
                            </div>
                            <div className="p-1.5 rounded-lg border bg-white/[0.04] border-white/[0.06]" data-id="element-2022">
                              <ChevronRight className="w-4 h-4 text-zinc-400" data-id="element-2023" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Events List */}
                      <div className="flex-1 overflow-y-auto px-6 pb-28 space-y-3" data-id="element-2024">
                        {[{
                    id: 1,
                    title: 'Product Sync',
                    time: '10:00 AM – 11:00 AM',
                    type: 'video',
                    location: 'Google Meet',
                    attendees: ['A', 'S', 'M'],
                    aiManaged: false,
                    accent: 'bg-blue-500'
                  }, {
                    id: 2,
                    title: 'Client Discovery Call',
                    time: '1:30 PM – 2:15 PM',
                    type: 'phone',
                    location: '+1 (555) 012-3456',
                    attendees: ['A', 'C'],
                    aiManaged: true,
                    accent: 'bg-vexu'
                  }, {
                    id: 3,
                    title: 'Design Review',
                    time: '3:00 PM – 4:00 PM',
                    type: 'video',
                    location: 'Zoom',
                    attendees: ['D'],
                    aiManaged: false,
                    accent: 'bg-purple-500'
                  }, {
                    id: 4,
                    title: 'Investor Update',
                    time: '5:00 PM – 5:30 PM',
                    type: 'video',
                    location: 'Google Meet',
                    attendees: ['A', 'B'],
                    aiManaged: true,
                    accent: 'bg-vexu'
                  }].map((event, i) => <motion.div key={event.id} initial={{
                    opacity: 0,
                    y: 12
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    boxShadow: highlightedCalFeature === i ? '0 0 20px rgba(0,200,83,0.3)' : 'none',
                    borderColor: highlightedCalFeature === i ? 'rgba(0,200,83,0.5)' : 'rgba(255,255,255,0.07)'
                  }} transition={{
                    delay: i * 0.1,
                    duration: 0.3
                  }} className="backdrop-blur-xl border rounded-2xl p-4 relative overflow-hidden bg-white/[0.03]" data-id="element-2025">
                            {event.aiManaged && <div className="absolute top-0 right-0 text-[9px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-bl-xl border-b border-l bg-vexu/10 text-vexu border-vexu/15" data-id="element-2026">
                                AI Scheduled
                              </div>}
                            <div className="flex items-start gap-3.5" data-id="element-2027">
                              <div className={`w-1 h-14 rounded-full mt-0.5 ${event.accent} opacity-80`} data-id="element-2028" />
                              <div className="flex-1 min-w-0" data-id="element-2029">
                                <h3 className="font-semibold text-[15px] mb-1.5 truncate pr-16 text-white" data-id="element-2030">
                                  {event.title}
                                </h3>
                                <div className="flex items-center gap-1.5 text-xs mb-3 text-zinc-400" data-id="element-2031">
                                  <Clock className="w-3.5 h-3.5 shrink-0" data-id="element-2032" />
                                  <span data-id="element-2033">{event.time}</span>
                                </div>
                                <div className="flex items-center gap-3" data-id="element-2034">
                                  <div className="flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-lg border truncate bg-white/[0.04] border-white/[0.06] text-zinc-300" data-id="element-2035">
                                    {event.type === 'video' ? <Video className="w-3 h-3 shrink-0" data-id="element-2036" /> : <Phone className="w-3 h-3 shrink-0" data-id="element-2037" />}
                                    <span className="truncate" data-id="element-2038">
                                      {event.location}
                                    </span>
                                  </div>
                                  <div className="flex -space-x-1.5" data-id="element-2039">
                                    {event.attendees.map((att, j) => <div key={j} className="w-6 h-6 rounded-full border flex items-center justify-center text-[9px] font-bold bg-white/[0.06] border-[#0A0A0A] text-zinc-400" data-id="element-2040">
                                        {att}
                                      </div>)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>)}
                      </div>
                      {/* Bottom Tab Bar */}
                      <div className="absolute bottom-0 left-0 right-0 z-40 flex justify-center pb-5 px-4" data-id="element-2041">
                        <div className="w-full max-w-[290px] rounded-full px-2 py-[3px] backdrop-blur-2xl border bg-[#111111] border-white/[0.08] shadow-[0_-2px_12px_rgba(0,0,0,0.3),0_4px_24px_rgba(0,0,0,0.4)]" data-id="element-2042">
                          <div className="flex items-center justify-around" data-id="element-2043">
                            {[{
                        id: 'home',
                        label: 'Home'
                      }, {
                        id: 'calls',
                        label: 'Calls'
                      }, {
                        id: 'calendar',
                        label: 'Calendar'
                      }, {
                        id: 'profile',
                        label: 'Account'
                      }].map(tab => {
                        const isActive = tab.id === 'calendar';
                        return <div key={tab.id} className="relative flex flex-col items-center justify-center w-[50px] py-1 rounded-xl" data-id="element-2044">
                                  {isActive && <div className="absolute inset-0 rounded-xl bg-vexu/[0.08]" data-id="element-2045" />}
                                  <div className="relative z-10 flex items-center justify-center" data-id="element-2046">
                                    {tab.id === 'calls' ? <div className="w-7 h-7 rounded-full flex items-center justify-center bg-vexu/[0.06] border border-vexu/[0.1]" data-id="element-2047">
                                        <Phone className="w-[15px] h-[15px] text-vexu/60" strokeWidth={1.8} data-id="element-2048" />
                                      </div> : tab.id === 'calendar' ? <Calendar className={`w-[17px] h-[17px] ${isActive ? 'text-vexu' : 'text-zinc-500'}`} strokeWidth={isActive ? 2.2 : 1.6} data-id="element-2049" /> : <div data-id="element-2050">
                                        {tab.id === 'home' && <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-500" data-id="element-2051">
                                            <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" data-id="element-2052" />
                                            <polyline points="9 22 9 12 15 12 15 22" data-id="element-2053" />
                                          </svg>}
                                        {tab.id === 'profile' && <User className="w-[17px] h-[17px] text-zinc-500" strokeWidth={1.6} data-id="element-2054" />}
                                      </div>}
                                  </div>
                                  <span className={`relative z-10 text-[9px] font-medium mt-0.5 tracking-wide ${isActive ? 'text-vexu opacity-100' : 'text-zinc-500 opacity-45'}`} data-id="element-2055">
                                    {tab.label}
                                  </span>
                                </div>;
                      })}
                          </div>
                        </div>
                      </div>
                    </motion.div>}

                  {/* Ecosystem View (Phase 31) */}
                  {calStep === 'ecosystem' && <motion.div key="cal-eco" initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0
              }} className="absolute inset-0 flex flex-col items-center justify-center bg-[#0A0A0A] px-6" data-id="element-2056">
                      <motion.div animate={{
                  scale: [1, 1.05, 1]
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="w-24 h-24 rounded-full bg-vexu/10 border-2 border-vexu flex items-center justify-center mb-12 shadow-[0_0_40px_rgba(0,200,83,0.3)]" data-id="element-2057">
                        <Bot className="w-10 h-10 text-vexu" data-id="element-2058" />
                      </motion.div>

                      <div className="grid grid-cols-2 gap-4 w-full" data-id="element-2059">
                        {[{
                    icon: PhoneIncoming,
                    label: 'Answer',
                    desc: '24/7 Call Handling'
                  }, {
                    icon: Brain,
                    label: 'Understand',
                    desc: 'Contextual AI'
                  }, {
                    icon: Calendar,
                    label: 'Schedule',
                    desc: 'Autonomous Booking'
                  }, {
                    icon: Shield,
                    label: 'Protect',
                    desc: 'Spam Blocking'
                  }].map((item, i) => <motion.div key={item.label} initial={{
                    opacity: 0,
                    y: 20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: i * 0.2
                  }} className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-4 flex flex-col items-center text-center" data-id="element-2060">
                            <item.icon className="w-6 h-6 text-vexu mb-2" data-id="element-2061" />
                            <span className="text-sm font-bold text-white mb-1" data-id="element-2062">
                              {item.label}
                            </span>
                            <span className="text-[10px] text-zinc-400" data-id="element-2063">
                              {item.desc}
                            </span>
                          </motion.div>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>}
          </AnimatePresence>
        </motion.div>

        {/* --- FLOATING TAGS (Phases 15-19) --- */}
        <AnimatePresence mode="wait" data-id="element-2064">
          {phase === 15 && <motion.div key="tags-15" className="absolute inset-0 pointer-events-none z-30" data-id="element-2065">
              {[{
            text: 'Easy Registration',
            x: -200,
            y: -120,
            rotate: -3,
            delay: 0.4,
            side: 'left' as const
          }, {
            text: 'Get Started in Seconds',
            x: 200,
            y: -80,
            rotate: 2,
            delay: 0.8,
            side: 'right' as const
          }, {
            text: 'Your AI Assistant Awaits',
            x: -200,
            y: 80,
            rotate: -2,
            delay: 1.2,
            side: 'left' as const
          }].map((tag, i) => <motion.div key={tag.text} initial={{
            opacity: 0,
            scale: 0.7,
            filter: 'blur(10px)',
            rotate: tag.rotate - 5
          }} animate={{
            opacity: 1,
            scale: 1,
            filter: 'blur(0px)',
            rotate: tag.rotate
          }} exit={{
            opacity: 0,
            scale: 0.85,
            filter: 'blur(8px)'
          }} transition={{
            delay: tag.delay,
            duration: 1,
            ease
          }} className="absolute" style={{
            left: tag.side === 'left' ? `calc(50% - 190px)` : `calc(50% + 190px)`,
            top: `calc(50% + ${tag.y}px)`,
            transform: tag.side === 'left' ? 'translateX(-100%)' : 'translateX(0%)'
          }} data-id="element-2066">
                  <motion.div animate={{
              y: [0, -8, 0],
              rotate: [tag.rotate, tag.rotate + 1.5, tag.rotate]
            }} transition={{
              duration: 4 + i * 0.6,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="px-5 py-2.5 rounded-full bg-white/[0.03] backdrop-blur-2xl border border-white/[0.07] shadow-[0_4px_30px_rgba(0,200,83,0.04)]" data-id="element-2067">
                    <span className="text-[11px] font-medium tracking-widest uppercase text-zinc-400 whitespace-nowrap" data-id="element-2068">
                      {tag.text}
                    </span>
                  </motion.div>
                </motion.div>)}
            </motion.div>}

          {phase === 16 && <motion.div key="tags-16" className="absolute inset-0 pointer-events-none z-30" data-id="element-2069">
              {[{
            text: 'Secure Verification',
            y: -100,
            rotate: 2,
            delay: 0.3,
            side: 'right' as const
          }, {
            text: 'Your Number. Your AI.',
            y: -10,
            rotate: -2.5,
            delay: 0.7,
            side: 'left' as const
          }, {
            text: 'Verified in Seconds',
            y: 100,
            rotate: 1.5,
            delay: 1.1,
            side: 'right' as const
          }].map((tag, i) => <motion.div key={tag.text} initial={{
            opacity: 0,
            scale: 0.7,
            filter: 'blur(10px)',
            rotate: tag.rotate - 5
          }} animate={{
            opacity: 1,
            scale: 1,
            filter: 'blur(0px)',
            rotate: tag.rotate
          }} exit={{
            opacity: 0,
            scale: 0.85,
            filter: 'blur(8px)'
          }} transition={{
            delay: tag.delay,
            duration: 1,
            ease
          }} className="absolute" style={{
            left: tag.side === 'left' ? `calc(50% - 190px)` : `calc(50% + 190px)`,
            top: `calc(50% + ${tag.y}px)`,
            transform: tag.side === 'left' ? 'translateX(-100%)' : 'translateX(0%)'
          }} data-id="element-2070">
                  <motion.div animate={{
              y: [0, -7, 0],
              rotate: [tag.rotate, tag.rotate - 1.5, tag.rotate]
            }} transition={{
              duration: 3.5 + i * 0.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="px-5 py-2.5 rounded-full bg-white/[0.03] backdrop-blur-2xl border border-white/[0.07] shadow-[0_4px_30px_rgba(0,200,83,0.04)]" data-id="element-2071">
                    <span className="text-[11px] font-medium tracking-widest uppercase text-zinc-400 whitespace-nowrap" data-id="element-2072">
                      {tag.text}
                    </span>
                  </motion.div>
                </motion.div>)}
            </motion.div>}

          {(phase === 18 || phase === 19) && <motion.div key="tags-18" className="absolute inset-0 pointer-events-none z-30" data-id="element-2073">
              {[{
            text: 'Busy? Vexu Answers.',
            y: -120,
            rotate: -2,
            delay: 0.5,
            side: 'left' as const
          }, {
            text: 'Your AI Is One Tap Away',
            y: -20,
            rotate: 2.5,
            delay: 0.9,
            side: 'right' as const
          }, {
            text: 'Never Miss Why They Called',
            y: 100,
            rotate: -1.5,
            delay: 1.3,
            side: 'left' as const
          }].map((tag, i) => <motion.div key={tag.text} initial={{
            opacity: 0,
            scale: 0.7,
            filter: 'blur(10px)',
            rotate: tag.rotate - 5
          }} animate={{
            opacity: 1,
            scale: 1,
            filter: 'blur(0px)',
            rotate: tag.rotate
          }} exit={{
            opacity: 0,
            scale: 0.85,
            filter: 'blur(8px)'
          }} transition={{
            delay: tag.delay,
            duration: 1,
            ease
          }} className="absolute" style={{
            left: tag.side === 'left' ? `calc(50% - 190px)` : `calc(50% + 190px)`,
            top: `calc(50% + ${tag.y}px)`,
            transform: tag.side === 'left' ? 'translateX(-100%)' : 'translateX(0%)'
          }} data-id="element-2074">
                  <motion.div animate={{
              y: [0, -9, 0],
              rotate: [tag.rotate, tag.rotate + 2, tag.rotate]
            }} transition={{
              duration: 4.5 + i * 0.4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="px-5 py-2.5 rounded-full bg-white/[0.03] backdrop-blur-2xl border border-white/[0.07] shadow-[0_4px_30px_rgba(0,200,83,0.04)]" data-id="element-2075">
                    <span className="text-[11px] font-medium tracking-widest uppercase text-zinc-400 whitespace-nowrap" data-id="element-2076">
                      {tag.text}
                    </span>
                  </motion.div>
                </motion.div>)}
            </motion.div>}

          {/* Phase 21-22 Tags */}
          {(phase === 21 || phase === 22) && <motion.div key="tags-21" className="absolute inset-0 pointer-events-none z-30" data-id="element-2077">
              {[{
            text: 'Easy Setup',
            y: -120,
            rotate: -2,
            delay: 0.4,
            side: 'left' as const
          }, {
            text: 'Create Custom Groups',
            y: -20,
            rotate: 2.5,
            delay: 0.8,
            side: 'right' as const
          }, {
            text: 'Tailored AI Responses',
            y: 100,
            rotate: -1.5,
            delay: 1.2,
            side: 'left' as const
          }].map((tag, i) => <motion.div key={tag.text} initial={{
            opacity: 0,
            scale: 0.7,
            filter: 'blur(10px)',
            rotate: tag.rotate - 5
          }} animate={{
            opacity: 1,
            scale: 1,
            filter: 'blur(0px)',
            rotate: tag.rotate
          }} exit={{
            opacity: 0,
            scale: 0.85,
            filter: 'blur(8px)'
          }} transition={{
            delay: tag.delay,
            duration: 1,
            ease
          }} className="absolute" style={{
            left: tag.side === 'left' ? `calc(50% - 190px)` : `calc(50% + 190px)`,
            top: `calc(50% + ${tag.y}px)`,
            transform: tag.side === 'left' ? 'translateX(-100%)' : 'translateX(0%)'
          }} data-id="element-2078">
                  <motion.div animate={{
              y: [0, -9, 0],
              rotate: [tag.rotate, tag.rotate + 2, tag.rotate]
            }} transition={{
              duration: 4.5 + i * 0.4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="px-5 py-2.5 rounded-full bg-white/[0.03] backdrop-blur-2xl border border-white/[0.07] shadow-[0_4px_30px_rgba(0,200,83,0.04)]" data-id="element-2079">
                    <span className="text-[11px] font-medium tracking-widest uppercase text-zinc-400 whitespace-nowrap" data-id="element-2080">
                      {tag.text}
                    </span>
                  </motion.div>
                </motion.div>)}
            </motion.div>}

          {/* Phase 23-24 Tags */}
          {(phase === 23 || phase === 24) && <motion.div key="tags-23" className="absolute inset-0 pointer-events-none z-30" data-id="element-2081">
              {[{
            text: 'Speak Their Language',
            y: -100,
            rotate: 2,
            delay: 0.3,
            side: 'right' as const
          }, {
            text: 'AI Scheduling Enabled',
            y: -10,
            rotate: -2.5,
            delay: 0.7,
            side: 'left' as const
          }, {
            text: 'Train Your Assistant',
            y: 100,
            rotate: 1.5,
            delay: 1.1,
            side: 'right' as const
          }].map((tag, i) => <motion.div key={tag.text} initial={{
            opacity: 0,
            scale: 0.7,
            filter: 'blur(10px)',
            rotate: tag.rotate - 5
          }} animate={{
            opacity: 1,
            scale: 1,
            filter: 'blur(0px)',
            rotate: tag.rotate
          }} exit={{
            opacity: 0,
            scale: 0.85,
            filter: 'blur(8px)'
          }} transition={{
            delay: tag.delay,
            duration: 1,
            ease
          }} className="absolute" style={{
            left: tag.side === 'left' ? `calc(50% - 190px)` : `calc(50% + 190px)`,
            top: `calc(50% + ${tag.y}px)`,
            transform: tag.side === 'left' ? 'translateX(-100%)' : 'translateX(0%)'
          }} data-id="element-2082">
                  <motion.div animate={{
              y: [0, -7, 0],
              rotate: [tag.rotate, tag.rotate - 1.5, tag.rotate]
            }} transition={{
              duration: 3.5 + i * 0.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="px-5 py-2.5 rounded-full bg-white/[0.03] backdrop-blur-2xl border border-white/[0.07] shadow-[0_4px_30px_rgba(0,200,83,0.04)]" data-id="element-2083">
                    <span className="text-[11px] font-medium tracking-widest uppercase text-zinc-400 whitespace-nowrap" data-id="element-2084">
                      {tag.text}
                    </span>
                  </motion.div>
                </motion.div>)}
            </motion.div>}

          {/* Phase 25 Tags */}
          {phase === 25 && <motion.div key="tags-25" className="absolute inset-0 pointer-events-none z-30" data-id="element-2085">
              {[{
            text: 'Choose Exactly Who Belongs',
            y: -120,
            rotate: -3,
            delay: 0.4,
            side: 'left' as const
          }, {
            text: 'Every Group. Unique AI.',
            y: -20,
            rotate: 2,
            delay: 0.8,
            side: 'right' as const
          }, {
            text: 'Premium Personalization',
            y: 80,
            rotate: -2,
            delay: 1.2,
            side: 'left' as const
          }].map((tag, i) => <motion.div key={tag.text} initial={{
            opacity: 0,
            scale: 0.7,
            filter: 'blur(10px)',
            rotate: tag.rotate - 5
          }} animate={{
            opacity: 1,
            scale: 1,
            filter: 'blur(0px)',
            rotate: tag.rotate
          }} exit={{
            opacity: 0,
            scale: 0.85,
            filter: 'blur(8px)'
          }} transition={{
            delay: tag.delay,
            duration: 1,
            ease
          }} className="absolute" style={{
            left: tag.side === 'left' ? `calc(50% - 190px)` : `calc(50% + 190px)`,
            top: `calc(50% + ${tag.y}px)`,
            transform: tag.side === 'left' ? 'translateX(-100%)' : 'translateX(0%)'
          }} data-id="element-2086">
                  <motion.div animate={{
              y: [0, -8, 0],
              rotate: [tag.rotate, tag.rotate + 1.5, tag.rotate]
            }} transition={{
              duration: 4 + i * 0.6,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="px-5 py-2.5 rounded-full bg-white/[0.03] backdrop-blur-2xl border border-white/[0.07] shadow-[0_4px_30px_rgba(0,200,83,0.04)]" data-id="element-2087">
                    <span className="text-[11px] font-medium tracking-widest uppercase text-zinc-400 whitespace-nowrap" data-id="element-2088">
                      {tag.text}
                    </span>
                  </motion.div>
                </motion.div>)}
            </motion.div>}

          {/* Phase 28-29 Tags */}
          {(phase === 28 || phase === 29) && <motion.div key="tags-28" className="absolute inset-0 pointer-events-none z-30" data-id="element-2089">
              {[{
            text: 'Booked by Vexu',
            y: -120,
            rotate: -2,
            delay: 0.4,
            side: 'left' as const
          }, {
            text: 'Calendar-Aware Intelligence',
            y: -20,
            rotate: 2.5,
            delay: 0.8,
            side: 'right' as const
          }, {
            text: 'Your Personal Agent',
            y: 100,
            rotate: -1.5,
            delay: 1.2,
            side: 'left' as const
          }].map((tag, i) => <motion.div key={tag.text} initial={{
            opacity: 0,
            scale: 0.7,
            filter: 'blur(10px)',
            rotate: tag.rotate - 5
          }} animate={{
            opacity: 1,
            scale: 1,
            filter: 'blur(0px)',
            rotate: tag.rotate
          }} exit={{
            opacity: 0,
            scale: 0.85,
            filter: 'blur(8px)'
          }} transition={{
            delay: tag.delay,
            duration: 1,
            ease
          }} className="absolute" style={{
            left: tag.side === 'left' ? `calc(50% - 190px)` : `calc(50% + 190px)`,
            top: `calc(50% + ${tag.y}px)`,
            transform: tag.side === 'left' ? 'translateX(-100%)' : 'translateX(0%)'
          }} data-id="element-2090">
                  <motion.div animate={{
              y: [0, -9, 0],
              rotate: [tag.rotate, tag.rotate + 2, tag.rotate]
            }} transition={{
              duration: 4.5 + i * 0.4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="px-5 py-2.5 rounded-full bg-white/[0.03] backdrop-blur-2xl border border-white/[0.07] shadow-[0_4px_30px_rgba(0,200,83,0.04)]" data-id="element-2091">
                    <span className="text-[11px] font-medium tracking-widest uppercase text-zinc-400 whitespace-nowrap" data-id="element-2092">
                      {tag.text}
                    </span>
                  </motion.div>
                </motion.div>)}
            </motion.div>}

          {/* Phase 30-31 Tags */}
          {(phase === 30 || phase === 31) && <motion.div key="tags-30" className="absolute inset-0 pointer-events-none z-30" data-id="element-2093">
              {[{
            text: 'Conflict-Free Scheduling',
            y: -100,
            rotate: 2,
            delay: 0.3,
            side: 'right' as const
          }, {
            text: 'Permission-Based Access',
            y: -10,
            rotate: -2.5,
            delay: 0.7,
            side: 'left' as const
          }, {
            text: 'Autonomous Time Management',
            y: 100,
            rotate: 1.5,
            delay: 1.1,
            side: 'right' as const
          }].map((tag, i) => <motion.div key={tag.text} initial={{
            opacity: 0,
            scale: 0.7,
            filter: 'blur(10px)',
            rotate: tag.rotate - 5
          }} animate={{
            opacity: 1,
            scale: 1,
            filter: 'blur(0px)',
            rotate: tag.rotate
          }} exit={{
            opacity: 0,
            scale: 0.85,
            filter: 'blur(8px)'
          }} transition={{
            delay: tag.delay,
            duration: 1,
            ease
          }} className="absolute" style={{
            left: tag.side === 'left' ? `calc(50% - 190px)` : `calc(50% + 190px)`,
            top: `calc(50% + ${tag.y}px)`,
            transform: tag.side === 'left' ? 'translateX(-100%)' : 'translateX(0%)'
          }} data-id="element-2094">
                  <motion.div animate={{
              y: [0, -7, 0],
              rotate: [tag.rotate, tag.rotate - 1.5, tag.rotate]
            }} transition={{
              duration: 3.5 + i * 0.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="px-5 py-2.5 rounded-full bg-white/[0.03] backdrop-blur-2xl border border-white/[0.07] shadow-[0_4px_30px_rgba(0,200,83,0.04)]" data-id="element-2095">
                    <span className="text-[11px] font-medium tracking-widest uppercase text-zinc-400 whitespace-nowrap" data-id="element-2096">
                      {tag.text}
                    </span>
                  </motion.div>
                </motion.div>)}
            </motion.div>}
        </AnimatePresence>

        {/* --- CINEMATIC TYPOGRAPHY OVERLAYS --- */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20" data-id="element-2097">
          <AnimatePresence mode="wait" data-id="element-2098">
            {phase === 3 && <motion.div key="text-3" initial={{
            opacity: 0,
            filter: 'blur(20px)',
            scale: 0.9
          }} animate={{
            opacity: 1,
            filter: 'blur(0px)',
            scale: 1
          }} exit={{
            opacity: 0,
            filter: 'blur(10px)',
            scale: 1.05
          }} transition={{
            duration: 1.5,
            ease
          }} className="text-center px-6" data-id="element-2099">
                <h2 className="text-4xl md:text-6xl font-light tracking-[0.05em] text-white drop-shadow-2xl" data-id="element-2100">
                  Every missed call...
                  <br data-id="element-2101" />
                  <motion.span initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 1.5,
                duration: 1
              }} className="font-serif italic text-zinc-400" data-id="element-2102">
                    has a reason.
                  </motion.span>
                </h2>
              </motion.div>}

            {phase === 4 && <motion.div key="text-4" initial={{
            opacity: 0,
            filter: 'blur(20px)',
            scale: 0.9
          }} animate={{
            opacity: 1,
            filter: 'blur(0px)',
            scale: 1
          }} exit={{
            opacity: 0,
            filter: 'blur(10px)',
            scale: 1.05
          }} transition={{
            duration: 1.5,
            ease
          }} className="text-center px-6" data-id="element-2103">
                <h2 className="text-4xl md:text-6xl font-medium tracking-tight text-white drop-shadow-2xl" data-id="element-2104">
                  But until now...
                  <br data-id="element-2105" />
                  <motion.span initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 1.5,
                duration: 1
              }} className="text-red-400" data-id="element-2106">
                    You never knew why.
                  </motion.span>
                </h2>
              </motion.div>}

            {phase === 7 && <motion.div key="text-7" initial={{
            opacity: 0,
            filter: 'blur(20px)',
            y: 20
          }} animate={{
            opacity: 1,
            filter: 'blur(0px)',
            y: 0
          }} exit={{
            opacity: 0,
            filter: 'blur(10px)',
            y: -20
          }} transition={{
            duration: 1.5,
            ease
          }} className="text-center px-6 flex flex-col items-center" data-id="element-2107">
                <h2 className="text-5xl md:text-7xl font-bold tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white to-white/60 drop-shadow-2xl mb-4" data-id="element-2108">
                  Never miss a call...
                </h2>
                <motion.h3 initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 1.5,
              duration: 1.5
            }} className="text-3xl md:text-4xl font-light text-vexu tracking-wide" data-id="element-2109">
                  without knowing why.
                </motion.h3>
              </motion.div>}

            {phase === 12 && <motion.div key="text-12" initial={{
            opacity: 0,
            filter: 'blur(20px)',
            scale: 0.9
          }} animate={{
            opacity: 1,
            filter: 'blur(0px)',
            scale: 1
          }} exit={{
            opacity: 0,
            filter: 'blur(10px)',
            scale: 1.05
          }} transition={{
            duration: 1.5,
            ease
          }} className="text-center px-6" data-id="element-2110">
                <h2 className="text-4xl md:text-6xl font-medium tracking-tight text-white drop-shadow-2xl" data-id="element-2111">
                  You can't always answer.
                  <br data-id="element-2112" />
                  <motion.span initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 2,
                duration: 1
              }} className="text-vexu font-bold" data-id="element-2113">
                    But Vexu can.
                  </motion.span>
                </h2>
              </motion.div>}

            {phase === 13 && <motion.div key="text-13" initial={{
            opacity: 0,
            filter: 'blur(20px)',
            scale: 0.9
          }} animate={{
            opacity: 1,
            filter: 'blur(0px)',
            scale: 1
          }} exit={{
            opacity: 0,
            filter: 'blur(10px)',
            scale: 1.05
          }} transition={{
            duration: 1.5,
            ease
          }} className="text-center px-6" data-id="element-2114">
                <h2 className="text-5xl md:text-7xl font-light tracking-tight text-white drop-shadow-2xl" data-id="element-2115">
                  Vexu answers.
                  <br data-id="element-2116" />
                  <motion.span initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 1.5,
                duration: 1
              }} className="font-medium text-transparent bg-clip-text bg-gradient-to-r from-vexu to-emerald-300" data-id="element-2117">
                    Vexu understands.
                  </motion.span>
                </h2>
              </motion.div>}

            {phase === 14 && <motion.div key="text-14" initial={{
            opacity: 0,
            filter: 'blur(20px)',
            y: 20
          }} animate={{
            opacity: 1,
            filter: 'blur(0px)',
            y: 0
          }} transition={{
            duration: 2,
            ease
          }} className="text-center px-6 flex flex-col items-center" data-id="element-2118">
                <h2 className="text-4xl md:text-6xl font-medium tracking-tight text-white drop-shadow-2xl mb-4" data-id="element-2119">
                  Getting started?
                </h2>
                <motion.h3 initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1.5,
              duration: 1.5
            }} className="text-3xl md:text-5xl font-light text-vexu tracking-wide" data-id="element-2120">
                  Effortless.
                </motion.h3>
              </motion.div>}

            {phase === 20 && <motion.div key="text-20" initial={{
            opacity: 0,
            filter: 'blur(20px)',
            y: 20
          }} animate={{
            opacity: 1,
            filter: 'blur(0px)',
            y: 0
          }} exit={{
            opacity: 0,
            filter: 'blur(10px)',
            y: -20
          }} transition={{
            duration: 2,
            ease
          }} className="text-center px-6 flex flex-col items-center" data-id="element-2121">
                <h2 className="text-6xl md:text-8xl font-bold tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white to-white/40 drop-shadow-2xl mb-6" data-id="element-2122">
                  Personalize Everything.
                </h2>
                <motion.h3 initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1.5,
              duration: 1.5
            }} className="text-3xl md:text-5xl font-light text-vexu tracking-wide" data-id="element-2123">
                  For Every Caller.
                </motion.h3>
              </motion.div>}

            {phase === 26 && <motion.div key="text-26" initial={{
            opacity: 0,
            filter: 'blur(20px)',
            y: 20
          }} animate={{
            opacity: 1,
            filter: 'blur(0px)',
            y: 0
          }} exit={{
            opacity: 0,
            filter: 'blur(10px)',
            y: -20
          }} transition={{
            duration: 2,
            ease
          }} className="text-center px-6 flex flex-col items-center" data-id="element-2124">
                <h2 className="text-6xl md:text-8xl font-bold tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white to-white/40 drop-shadow-2xl mb-6" data-id="element-2125">
                  Available...
                </h2>
                <motion.h3 initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1.5,
              duration: 1.5
            }} className="text-3xl md:text-5xl font-light text-vexu tracking-wide" data-id="element-2126">
                  Even When You're Not.
                </motion.h3>
              </motion.div>}

            {phase === 27 && <motion.div key="text-27" initial={{
            opacity: 0,
            filter: 'blur(20px)',
            y: 20
          }} animate={{
            opacity: 1,
            filter: 'blur(0px)',
            y: 0
          }} exit={{
            opacity: 0,
            filter: 'blur(10px)',
            y: -20
          }} transition={{
            duration: 2,
            ease
          }} className="text-center px-6 flex flex-col items-center" data-id="element-2127">
                <h2 className="text-4xl md:text-6xl font-medium tracking-tight text-white drop-shadow-2xl mb-4" data-id="element-2128">
                  Vexu doesn't just respond…
                </h2>
                <motion.h3 initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1.5,
              duration: 1.5
            }} className="text-3xl md:text-5xl font-bold text-vexu tracking-wide" data-id="element-2129">
                  …it takes action.
                </motion.h3>
              </motion.div>}

            {phase === 32 && <motion.div key="text-32" initial={{
            opacity: 0,
            filter: 'blur(20px)',
            y: 20
          }} animate={{
            opacity: 1,
            filter: 'blur(0px)',
            y: 0
          }} transition={{
            duration: 2,
            ease
          }} className="text-center px-6 flex flex-col items-center" data-id="element-2130">
                <h2 className="text-6xl md:text-8xl font-bold tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white to-white/40 drop-shadow-2xl mb-6" data-id="element-2131">
                  Answer.
                </h2>
                <motion.h3 initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1.5,
              duration: 1.5
            }} className="text-4xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-vexu to-emerald-300 tracking-wide mb-4" data-id="element-2132">
                  Understand.
                </motion.h3>
                <motion.h3 initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 3,
              duration: 1.5
            }} className="text-5xl md:text-7xl font-bold text-white tracking-wide" data-id="element-2133">
                  Act.
                </motion.h3>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* --- CONTROLS --- */}

        <AnimatePresence data-id="element-2134">
          {phase === TOTAL_PHASES && <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 3,
          duration: 1.5,
          ease
        }} className="absolute bottom-16 left-0 right-0 flex flex-col items-center gap-4 z-50" data-id="element-2135">
              <button onClick={handleBackToApp} className="group flex items-center gap-2 px-8 py-4 rounded-full bg-vexu text-black font-bold text-sm hover:bg-vexu-400 transition-all active:scale-95 shadow-[0_0_30px_rgba(0,200,83,0.3)]" data-id="element-2136">
                <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" data-id="element-2137" />
                Enter Vexu App
              </button>
              <button onClick={handleReplay} className="flex items-center gap-2 px-6 py-3 rounded-full bg-transparent text-zinc-400 font-medium text-xs hover:text-white transition-colors" data-id="element-2138">
                <Play className="w-3 h-3" data-id="element-2139" />
                Replay Presentation
              </button>

              {/* Contact Details */}
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 4.5,
            duration: 1
          }} className="flex flex-col items-center gap-1.5 mt-2" data-id="element-2140">
                <span className="text-[10px] uppercase tracking-widest font-semibold text-zinc-600" data-id="element-2141">
                  Contact
                </span>
                <div className="flex items-center gap-4" data-id="element-2142">
                  <a href="mailto:fadi@vexu.ai" className="text-xs font-medium text-zinc-400 hover:text-vexu transition-colors" data-id="element-2143">
                    fadi@vexu.ai
                  </a>
                  <span className="text-zinc-700" data-id="element-2144">·</span>
                  <a href="tel:+971585377090" className="text-xs font-medium text-zinc-400 hover:text-vexu transition-colors" data-id="element-2145">
                    +971 58 537 7090
                  </a>
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>

        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10 z-50" data-id="element-2146">
          <motion.div className="h-full bg-vexu shadow-[0_0_10px_rgba(0,200,83,0.8)]" initial={{
          width: '0%'
        }} animate={{
          width: phase === TOTAL_PHASES ? '100%' : `${phase / TOTAL_PHASES * 100}%`
        }} transition={{
          duration: phase === TOTAL_PHASES ? 0.5 : PHASES[phase - 1]?.duration / 1000,
          ease: 'linear'
        }} data-id="element-2147" />
        </div>
      </div>
    </div>;
}